import { roofPanelPhoto } from "@/lib/utils";
import { useEffect, useState, useRef } from "react";
import { api, API, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import OrderFlow from "./OrderFlow";
import BookingWidget from "./BookingWidget";
import OutlookShare from "./OutlookShare";
import OrderConfirm from "./OrderConfirm";
import CompanyPositioning from "./CompanyPositioning";
import SignaturePad from "./SignaturePad";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import ProductSlider, { GaranzieBadges } from "./ProductSlider";
import { EnergyChartLegend, withAcquistata } from "./EnergyChartLegend";
import RoofMapView from "./RoofMapView";
import { hasPdfBg, hasDrawing, FALDA_COLORS } from "./RoofPdfView";
import { QRCodeSVG } from "qrcode.react";
import { FileDown, Cpu, ShieldCheck } from "lucide-react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine,
  BarChart, Cell, PieChart, Pie,
} from "recharts";
import {
  Loader2, SunMedium, BatteryCharging, Plug, Leaf, Zap, Gauge, PiggyBank,
  TrendingUp, Clock, FileText, ArrowRight, CheckCircle2, MapPin, Sparkles,
  PackageCheck, XCircle, Phone, Mail, CreditCard, ScrollText, Check, BarChart3, Handshake, Printer, Satellite, Ruler, Users, StickyNote, LayoutGrid, Paperclip, Download, ExternalLink, Palette, UserCheck, Network, Wrench, Star, ClipboardCheck,
} from "lucide-react";

const m2 = (v) => (v === null || v === undefined ? "—" : `${Number(v).toLocaleString("it-IT", { maximumFractionDigits: 1 })} m²`);
const orientLabel = (a) => {
  const t = [[180, "Sud"], [135, "Sud-Est"], [225, "Sud-Ovest"], [90, "Est"], [270, "Ovest"], [45, "Nord-Est"], [315, "Nord-Ovest"], [0, "Nord"], [360, "Nord"]];
  let best = t[0]; let bd = 999;
  t.forEach(([deg, l]) => { const d = Math.abs(deg - a); if (d < bd) { bd = d; best = [deg, l]; } });
  return best[1];
};
const EXCL_LABEL = { ostacolo: "Ostacolo", lucernario: "Lucernario", zona_non_utilizzabile: "Zona non utilizzabile" };
// Copertura mista: unisce i pannelli delle falde inclinate (panel_layout) e del tetto piano (flat_layout).
const combinedRoofPanels = (roof) => {
  const inc = roof?.panel_layout?.enabled ? (roof.panel_layout.panels || []) : [];
  const flat = roof?.flat_layout?.compatible ? (roof.flat_layout.panels || []) : [];
  return inc.concat(flat);
};
const combinedPanelLayout = (roof) => { const ps = combinedRoofPanels(roof); return { enabled: ps.length > 0, panels: ps }; };
const roofPanelCounts = (roof) => ({
  inc: roof?.panel_layout?.enabled ? (roof.panel_layout.panels || []).length : 0,
  flat: roof?.flat_layout?.compatible ? (roof.flat_layout.panels || []).length : 0,
  incK: roof?.panel_layout?.enabled ? (roof.panel_layout.summary?.kwp || 0) : 0,
  flatK: roof?.flat_layout?.compatible ? (roof.flat_layout.summary?.kwp || 0) : 0,
});
const roofIsMisto = (roof) => { const c = roofPanelCounts(roof); return c.inc > 0 && c.flat > 0; };
const MERC = 20037508.342789244;
const toMerc = (lat, lng) => {
  const x = (lng * MERC) / 180;
  const y = (Math.log(Math.tan(((90 + lat) * Math.PI) / 360)) / (Math.PI / 180)) * MERC / 180;
  return { x, y };
};

// Anteprima satellitare via Esri World Imagery (nessuna chiave, non soggetta al blocco EEA di Google),
// con overlay SVG dei poligoni delle falde ed esclusioni.
function esriRoofView(roof) {
  const falde = roof?.falde || [];
  const all = [];
  falde.forEach((f) => {
    (f.coordinates || []).forEach((c) => all.push(c));
    (f.esclusioni || []).forEach((e) => (e.coordinates || []).forEach((c) => all.push(c)));
  });
  if (all.length < 3) return null;
  const lats = all.map((p) => p.lat), lngs = all.map((p) => p.lng);
  const sw = { lat: Math.min(...lats), lng: Math.min(...lngs) };
  const ne = { lat: Math.max(...lats), lng: Math.max(...lngs) };
  const dLat = (ne.lat - sw.lat) || 0.0004, dLng = (ne.lng - sw.lng) || 0.0004;
  const S = sw.lat - dLat * 0.35, N = ne.lat + dLat * 0.35, Wl = sw.lng - dLng * 0.35, E = ne.lng + dLng * 0.35;
  const mSW = toMerc(S, Wl), mNE = toMerc(N, E);
  const AR = 1.6;
  const cx = (mSW.x + mNE.x) / 2, cy = (mSW.y + mNE.y) / 2;
  let mw = mNE.x - mSW.x, mh = mNE.y - mSW.y;
  if (mw / mh > AR) mh = mw / AR; else mw = mh * AR;
  // ArcGIS: richiediamo risoluzione doppia (min ~1024px, cap 2048) per un'immagine molto più nitida.
  const RES = 0.17;
  let W = Math.round(mw / RES);
  if (W < 1024) { const s = (1024 * RES) / mw; mw *= s; mh *= s; W = 1024; }
  W = Math.min(W, 2048);
  const H = Math.round(W / AR);
  const ex = { xmin: cx - mw / 2, ymin: cy - mh / 2, xmax: cx + mw / 2, ymax: cy + mh / 2 };
  const invMerc = (x, y) => ({
    lng: (x / MERC) * 180,
    lat: (180 / Math.PI) * (2 * Math.atan(Math.exp(((y / MERC) * 180) * Math.PI / 180)) - Math.PI / 2),
  });
  const so = roof?.sat_offset || {}, k = 1 / Math.cos((((S + N) / 2) * Math.PI) / 180);
  const ox = (Number(so.dx) || 0) * k, oy = (Number(so.dy) || 0) * k;
  const swLL = invMerc(ex.xmin - ox, ex.ymin - oy), neLL = invMerc(ex.xmax - ox, ex.ymax - oy);
  const bbox = `${swLL.lng},${swLL.lat},${neLL.lng},${neLL.lat}`;
  // Proxy backend con lieve correzione colore (attenua la dominante gialla/calda dell'imagery Esri).
  const url = `${API}/roof/satellite?bbox=${bbox}&w=${W}&h=${H}`;
  const project = (lat, lng) => {
    const m = toMerc(lat, lng);
    return { x: ((m.x - ex.xmin) / (ex.xmax - ex.xmin)) * W, y: ((ex.ymax - m.y) / (ex.ymax - ex.ymin)) * H };
  };
  return { url, W, H, project, falde };
}

const eur = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }));
const eur2 = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }));

function SopralluogoCard({ sopralluogo, publicToken, leadId, onToggle, prezzo }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const [nome, setNome] = useState("");
  const [sigEmpty, setSigEmpty] = useState(true);
  const [saving, setSaving] = useState(false);
  const sigRef = useRef(null);
  const s = sopralluogo || {};
  const b = s.breakdown || {};
  const firmato = !!s.firma?.accettato;
  const acceptUrl = publicToken ? `/public/outlook/${publicToken}/sopralluogo/accept` : `/leads/${leadId}/sopralluogo/accept`;
  const confirmSign = async () => {
    const firma = sigRef.current?.toDataURL();
    if (!firma) { toast.error("Apponi la firma nel riquadro"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(acceptUrl, { nome: nome.trim(), firma_img: firma });
      onToggle && onToggle(data.sopralluogo);
      setOpen(false);
      toast.success("Condizioni sopralluogo firmate");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/public/outlook/${publicToken}/sopralluogo/checkout`, { origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url; else setBusy(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  const toggle = async (v) => {
    try { const { data } = await api.post(`/leads/${leadId}/sopralluogo-offer`, { enabled: v }); onToggle && onToggle(data.sopralluogo); toast.success(v ? "Offerta sopralluogo attivata" : "Offerta sopralluogo disattivata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openBlobDoc = async (url, errMsg) => {
    const tab = window.open("", "_blank");
    try {
      const res = await api.get(url, { responseType: "blob" });
      const burl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (tab) tab.location.href = burl; else window.location.href = burl;
      setTimeout(() => URL.revokeObjectURL(burl), 60000);
    } catch (e) {
      if (tab) tab.close();
      toast.error(formatApiError(e.response?.data?.detail) || errMsg);
    }
  };
  const padDoc = () => openBlobDoc(`/leads/${leadId}/sopralluogo/pad.pdf`, "Errore download pad");
  const signedDoc = () => openBlobDoc(`/leads/${leadId}/sopralluogo/signed.pdf`, "PDF firmato non disponibile");
  return (
    <Section title="Sopralluogo tecnico" icon={ClipboardCheck} testid="outlook-sopralluogo">
      {!publicToken && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 rounded border border-border p-3" data-testid="sopralluogo-offer-toggle-row">
          <span className="text-sm">Proponi il pagamento del sopralluogo in questa proposta (prima del contratto)</span>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" variant="outline" onClick={padDoc} data-testid="download-sopralluogo-pad"><Download className="h-4 w-4 mr-1.5" /> Pad</Button>
            {firmato && s.signed_pdf && <Button size="sm" variant="outline" onClick={signedDoc} data-testid="download-sopralluogo-signed"><Download className="h-4 w-4 mr-1.5" /> Firmato</Button>}
            <Button size="sm" variant={s.offer_enabled ? "default" : "outline"} onClick={() => toggle(!s.offer_enabled)} data-testid="toggle-sopralluogo-offer">{s.offer_enabled ? "Attivo" : "Attiva"}</Button>
          </div>
        </div>
      )}
      {(publicToken ? s.offer_enabled : true) && (
        <>
          <p className="text-sm text-muted-foreground whitespace-pre-line mb-4" data-testid="sopralluogo-condizioni">{s.condizioni}</p>
          <div className="rounded border border-border p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Importo sopralluogo</div>
                <div className="text-2xl font-heading font-bold" data-testid="sopralluogo-importo">{eur2(s.importo)}</div>
                {!publicToken && (
                  <div className="text-[11px] text-muted-foreground mt-1 leading-relaxed" data-testid="sopralluogo-breakdown">
                    Trasporto {eur2(b.trasporto)} = {b.eur_km ?? 0} €/km × {b.distanza_km ?? 0} km (andata e ritorno).{" "}
                    Sede più vicina: {b.origin_type === "agenzia" ? <>agenzia abilitata «<b>{b.origin_label}</b>»</> : <>nostra sede TORRI</>}{b.one_way_km ? ` — ${b.one_way_km} km di sola andata` : ""}.{" "}
                    + Quota fissa {eur2(b.quota_fissa)}{b.fino_a_kw ? ` (impianti fino a ${b.fino_a_kw} kW)` : ""}.
                  </div>
                )}
              </div>
              {s.pagato && <Badge className="border-emerald-500/40 bg-emerald-500/10 text-emerald-500" data-testid="sopralluogo-pagato-badge">Pagato{s.commessa ? ` · ${s.commessa}` : ""}</Badge>}
            </div>
            {!s.pagato && publicToken && (
              <div className="mt-4 pt-4 border-t border-border space-y-3">
                {firmato ? (
                  <div className="flex items-center gap-2 text-sm text-emerald-500" data-testid="sopralluogo-firmato"><Check className="h-4 w-4" /> Condizioni firmate da <b>{s.firma?.nome}</b></div>
                ) : (
                  <>
                    <div>
                      <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Nome e cognome del firmatario</label>
                      <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome Cognome" data-testid="sopralluogo-nome" className="mt-1" />
                    </div>
                    <label className="flex items-start gap-2 cursor-pointer text-xs text-muted-foreground leading-relaxed">
                      <Checkbox checked={checked} onCheckedChange={setChecked} data-testid="sopralluogo-accept-checkbox" className="mt-0.5" />
                      <span>Ho letto e accetto le <b className="text-foreground">Condizioni del sopralluogo</b> sopra riportate.</span>
                    </label>
                    <Button onClick={() => { if (!nome.trim()) { toast.error("Inserisci nome e cognome"); return; } if (!checked) { toast.error("Spunta la casella di accettazione"); return; } setSigEmpty(true); setOpen(true); }} data-testid="sign-sopralluogo-btn"><ClipboardCheck className="h-4 w-4 mr-2" /> Firma il sopralluogo</Button>
                  </>
                )}
                <Button onClick={pay} disabled={busy || !firmato} data-testid="pay-sopralluogo-btn" className="w-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Paga il sopralluogo"}</Button>
              </div>
            )}
          </div>
          {s.pagato && prezzo ? (
            <div className="mt-3 rounded border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm" data-testid="sopralluogo-saldo">
              <div className="flex justify-between py-0.5"><span className="text-muted-foreground">Prezzo impianto (IVA incl.)</span><span className="font-semibold">{eur2(prezzo)}</span></div>
              <div className="flex justify-between py-0.5 text-emerald-500"><span>Sopralluogo già versato</span><span className="font-semibold">− {eur2(s.pagato_importo || s.importo)}</span></div>
              <div className="flex justify-between py-1 mt-1 border-t border-emerald-500/20 text-base"><span className="font-semibold">Saldo residuo a contratto</span><span className="font-heading font-bold" data-testid="sopralluogo-saldo-residuo">{eur2(Math.max(0, prezzo - (s.pagato_importo || s.importo || 0)))}</span></div>
            </div>
          ) : null}
        </>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="sopralluogo-sign-dialog">
          <DialogHeader><DialogTitle className="font-heading">Firma le condizioni del sopralluogo</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Confermando, la firma viene registrata con data, ora e IP a fini probatori e viene generato il documento firmato scaricabile.</p>
          <SignaturePad ref={sigRef} onChange={setSigEmpty} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="sopralluogo-sign-cancel">Annulla</Button>
            <Button onClick={confirmSign} disabled={saving || sigEmpty} className="font-semibold" data-testid="sopralluogo-sign-confirm">{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ClipboardCheck className="h-4 w-4 mr-2" />} Conferma e firma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}
const kwh = (v) => (v === null || v === undefined ? "—" : `${Number(v).toLocaleString("it-IT")} kWh`);

const C_PROD = "#C99700";     // produzione FV (oro)
const C_CONS = "#6B7280";     // consumo (grigio)
const C_AUTO = "#FDD003";     // autoconsumo (giallo TORRI)
const C_ACQ = "#EF4444";      // energia acquistata dalla rete
const C_RID = "#5B8DEF";      // ritiro dedicato (RID) blu
const C_CER = "#4E9A6B";      // beneficio CER verde
const C_CUM = "#C99700";      // cumulato
const C_GRID = "#2A2A2A";
const C_AXIS = "#9CA3AF";
const C_REALE = "#4E9A6B";    // dato reale (verde)
const C_STIM = "#D9A441";     // dato stimato (ambra)

function ChartTooltip({ active, payload, label, unit = "" }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg border border-border bg-card/95 backdrop-blur px-3 py-2 text-xs shadow-lg">
      <div className="font-semibold mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-sm" style={{ background: p.color }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-medium">{Number(p.value).toLocaleString("it-IT")}{unit}</span>
        </div>
      ))}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent, hint }) {
  return (
    <div className="flex items-start gap-3 min-w-0">
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${accent ? "bg-primary/15" : "bg-secondary"}`}>
        <Icon className={`h-5 w-5 ${accent ? "text-primary" : "text-muted-foreground"}`} strokeWidth={1.5} />
      </div>
      <div className="min-w-0">
        <div className="text-xl sm:text-2xl font-heading font-bold leading-tight break-words">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
        {hint && <div className="stat-hint text-[11px] text-muted-foreground/80 leading-snug mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}

function BenefitCard({ color, title, desc, annuo, totale, anni, testid }) {
  const eur = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }));
  return (
    <div className="rounded-lg border border-border p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <span className="h-3 w-3 rounded-sm shrink-0" style={{ backgroundColor: color }} />
        <span className="text-sm font-semibold">{title}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-heading font-bold">{eur(annuo)}</span>
        <span className="text-xs text-muted-foreground">/anno</span>
      </div>
      <div className="text-xs text-muted-foreground mt-0.5">{eur(totale)} in {anni} anni</div>
      <p className="text-[11px] text-muted-foreground mt-2 leading-snug">{desc}</p>
    </div>
  );
}


function Section({ title, icon: Icon, children, testid, className = "", action = null }) {
  return (
    <Card className={`rounded-xl border-border p-6 pdf-slot ${className}`} data-testid={testid}>
      <div className="flex items-start justify-between gap-3 mb-6">
        <h2 className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-bold flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary" strokeWidth={1.5} /> {title}
        </h2>
        {action}
      </div>
      {children}
    </Card>
  );
}

function FeasStat({ label, value, accent, testid, badge }) {
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${accent ? "border-primary/40 bg-primary/5" : "border-border"}`}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
        <span>{label}</span>
        {badge && <span className="inline-block rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300 px-1.5 py-0.5 text-[8px] font-semibold tracking-normal leading-none">{badge}</span>}
      </div>
      <div className={`font-heading font-bold text-lg leading-tight ${accent ? "text-primary" : ""}`} data-testid={testid}>{value}</div>
    </div>
  );
}

// Percentuale di autoconsumo per mese mostrata come secondo asse X sotto il grafico.
// Etichetta a sinistra della riga delle percentuali (posizionata nel margine sinistro).
function AutoAxisLabel({ viewBox, fill = "#b8860b" }) {
  const vb = viewBox || {};
  return (
    <text x={(vb.x || 0) - 4} y={(vb.y || 0) + 11} textAnchor="end" fontSize={8} fontWeight={700} fill={fill}>% autocons.</text>
  );
}
function AutoKwhLabel({ viewBox, fill = "#3f7d3f" }) {
  const vb = viewBox || {};
  return (
    <text x={(vb.x || 0) - 4} y={(vb.y || 0) + 10} textAnchor="end" fontSize={7.5} fontWeight={700} fill={fill}>kWh autocons.</text>
  );
}
function AcqKwhLabel({ viewBox, fill = "#9a3b2e" }) {
  const vb = viewBox || {};
  return (
    <text x={(vb.x || 0) - 4} y={(vb.y || 0) + 10} textAnchor="end" fontSize={7.5} fontWeight={700} fill={fill}>kWh acquist.</text>
  );
}
function ProdKwhLabel({ viewBox, fill = "#C99700" }) {
  const vb = viewBox || {};
  return (
    <text x={(vb.x || 0) - 4} y={(vb.y || 0) + 10} textAnchor="end" fontSize={7.5} fontWeight={700} fill={fill}>kWh prod. FV</text>
  );
}


function PaybackSparkline({ curve, payback }) {
  if (!curve || curve.length < 2) return null;
  const W = 200, H = 52, pad = 4;
  const min = Math.min(...curve, 0), max = Math.max(...curve, 0);
  const range = (max - min) || 1;
  const n = curve.length;
  const xf = (i) => pad + (i * (W - 2 * pad)) / (n - 1);
  const yf = (v) => pad + (H - 2 * pad) * (1 - (v - min) / range);
  const pts = curve.map((v, i) => `${xf(i)},${yf(v)}`).join(" ");
  const zeroY = yf(0);
  const pbi = payback ? Math.min(Math.max(Math.round(payback) - 1, 0), n - 1) : null;
  return (
    <div className="mt-3" data-testid="payback-chart">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Ritorno investimento</div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-12 text-muted-foreground" preserveAspectRatio="none">
        <line x1={pad} y1={zeroY} x2={W - pad} y2={zeroY} stroke="currentColor" strokeOpacity="0.25" strokeDasharray="3 3" />
        <polyline points={pts} fill="none" stroke="hsl(var(--primary))" strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
        {pbi != null && <circle cx={xf(pbi)} cy={yf(curve[pbi])} r="3.5" fill="hsl(var(--primary))" />}
      </svg>
      {payback && <div className="text-[11px] text-muted-foreground mt-0.5">Rientro in <b className="text-foreground">{payback} anni</b></div>}
    </div>
  );
}


function LayoutSizingNote({ ll, copertura, fallback }) {
  const n = (v) => Number(v || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 });
  if (!ll) return <span className="block text-xs text-muted-foreground mt-1">{fallback}</span>;
  const limited = Number(ll.fabbisogno_kwp || 0) > Number(ll.kwp || 0);
  return (
    <span className="block text-xs mt-1 text-foreground/90" data-testid="layout-sizing-note">
      {limited
        ? <>Il tuo fabbisogno sarebbe di <b>{n(ll.fabbisogno_kwp)} kWp</b>, ma il layout sviluppato sul tetto ne ospita <b className="text-primary">{n(ll.kwp)} kWp</b> ({ll.count} moduli){copertura != null ? <>: l'impianto proposto copre il <b>{Math.round(copertura)}%</b> dei tuoi consumi</> : ""}.</>
        : <>L'impianto proposto ({n(ll.kwp)} kWp, {ll.count} moduli) corrisponde al layout sviluppato sul tuo tetto.</>}
    </span>
  );
}

function PreventiviConfronto({ items, publicToken, leadId, locked, interactive, onReload }) {
  const [busy, setBusy] = useState(null);
  const manLabel = (m) => {
    if (!m || !m.attiva) return "No";
    if (m.incluso) return `Inclusa${m.anni ? ` · ${m.anni} ${m.anni === 1 ? "anno" : "anni"}` : ""}`;
    if (m.su_preventivo) return "Su preventivo";
    return m.prezzo_annuo_iva != null ? `${eur(m.prezzo_annuo_iva)}/anno` : "Sì";
  };
  const scrollToOrder = () => setTimeout(() => document.querySelector("[data-testid='order-flow']")?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
  const select = async (pid, isActive) => {
    if (isActive) { scrollToOrder(); return; }
    if (busy) return;
    setBusy(pid);
    try {
      if (publicToken) await api.post(`/public/outlook/${publicToken}/select-preventivo/${pid}`);
      else await api.put(`/leads/${leadId}/preventivi/${pid}/activate`);
      toast.success("Soluzione selezionata");
      if (onReload) await onReload();
      scrollToOrder();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  return (
    <Section title="Confronta le soluzioni" icon={LayoutGrid} testid="outlook-preventivi-confronto">
      <p className="text-xs text-muted-foreground mb-4">Abbiamo preparato più configurazioni per il tuo impianto: confronta prezzo, dimensionamento e ritorno dell'investimento{interactive && !locked ? ", scegli quella più adatta a te e procedi con la firma e il pagamento" : " e scegli quella più adatta a te"}.</p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 pdf-atomic">
        {items.map((p) => (
          <div key={p.id} className={`rounded-xl border p-4 flex flex-col ${p.consigliato ? "border-amber-500/60 bg-amber-500/5" : p.active ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-border"}`} data-testid={`outlook-confronto-${p.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <span className="font-heading font-bold text-base">{p.label}</span>
              <div className="flex flex-col items-end gap-1 shrink-0">
                {p.consigliato && <span className="text-[10px] uppercase tracking-wide font-semibold text-amber-600 bg-amber-500/15 rounded-full px-2 py-0.5 flex items-center gap-1"><Star className="h-3 w-3" fill="currentColor" /> Consigliato</span>}
                {p.active && <span className="text-[10px] uppercase tracking-wide font-semibold text-primary bg-primary/10 rounded-full px-2 py-0.5">Selezionato</span>}
              </div>
            </div>
            <div className="mb-3">
              <div className="text-2xl font-heading font-bold text-primary leading-none">{eur(p.prezzo_iva)}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">IVA inclusa</div>
            </div>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center justify-between"><span className="text-muted-foreground flex items-center gap-1.5"><Zap className="h-3.5 w-3.5" /> Potenza</span><span className="font-semibold">{p.pv_kwp ? `${p.pv_kwp} kWp` : "—"}</span></li>
              <li className="flex items-center justify-between"><span className="text-muted-foreground flex items-center gap-1.5"><BatteryCharging className="h-3.5 w-3.5" /> Accumulo</span><span className="font-semibold">{p.storage_kwh ? `${p.storage_kwh} kWh` : "No"}</span></li>
              {p.produzione_annua != null && <li className="flex items-center justify-between"><span className="text-muted-foreground flex items-center gap-1.5"><SunMedium className="h-3.5 w-3.5" /> Produzione annua</span><span className="font-semibold">{kwh(p.produzione_annua)}</span></li>}
              {p.autoconsumo_pct != null && <li className="flex items-center justify-between"><span className="text-muted-foreground flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5" /> Autoconsumo</span><span className="font-semibold">{p.autoconsumo_pct}%</span></li>}
              {p.copertura_pct != null && <li className="flex items-center justify-between"><span className="text-muted-foreground flex items-center gap-1.5"><Gauge className="h-3.5 w-3.5" /> Copertura</span><span className="font-semibold">{p.copertura_pct}%</span></li>}
              {p.risp_medio != null && <li className="flex items-center justify-between"><span className="text-muted-foreground flex items-center gap-1.5"><PiggyBank className="h-3.5 w-3.5" /> Rendimento €/anno</span><span className="font-semibold">{eur(p.risp_medio)}</span></li>}
              {p.payback != null && <li className="flex items-center justify-between"><span className="text-muted-foreground flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> PBT</span><span className="font-semibold">{p.payback} anni</span></li>}
              <li className="flex items-center justify-between"><span className="text-muted-foreground flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5" /> Finanziamento</span><span className="font-semibold">{p.rata_mensile != null ? `${eur(p.rata_mensile)}/mese` : "No"}</span></li>
              <li className="flex items-center justify-between"><span className="text-muted-foreground flex items-center gap-1.5"><Wrench className="h-3.5 w-3.5" /> Manutenzione</span><span className="font-semibold">{manLabel(p.manutenzione)}</span></li>
              <li className="flex items-center justify-between"><span className="text-muted-foreground flex items-center gap-1.5"><Plug className="h-3.5 w-3.5" /> Stazione di ricarica</span><span className="font-semibold">{p.wallbox ? "Sì" : "No"}</span></li>
            </ul>
            {interactive && (
              <div className="mt-4 no-print">
                {locked ? (
                  p.active ? <div className="text-xs text-primary font-medium flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Soluzione confermata</div> : null
                ) : (
                  <Button onClick={() => select(p.id, p.active)} disabled={busy === p.id} variant={p.active ? "default" : "outline"} className="w-full font-semibold" data-testid={`outlook-confronto-select-${p.id}`}>
                    {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : (p.active ? "Firma e paga questa soluzione" : "Scegli e procedi")}
                  </Button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}


const RO_SECTIONS = [
  ["Richieste specifiche cliente", [["richieste_estetiche", "Preferenze estetiche"], ["zone_vietate", "Zone vietate"], ["orari_consentiti", "Orari consentiti"], ["giorni_non_lavorabili", "Giorni non lavorabili"], ["richieste_particolari", "Richieste particolari"], ["richieste_extra", "Richieste extra"]]],
  ["Accesso e logistica cantiere", [["accesso_mezzi", "Accesso mezzi"], ["parcheggio", "Parcheggio"], ["mezzi_speciali", "Mezzi speciali"], ["distanza_scarico", "Distanza scarico"], ["ostacoli", "Ostacoli"]]],
  ["Aspettative cliente", [["urgenza", "Urgenza"], ["data_richiesta", "Data richiesta"], ["sensibilita_ritardi", "Sensibilità a ritardi"]]],
  ["Note operative", [["note_operative", "Note"]]],
];

function ReportOperativoPanel({ leadId, initial }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial || {});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/leads/${leadId}/report-operativo`, { data: form });
      toast.success("Report operativo salvato");
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };
  const downloadPad = async () => {
    const t = toast.loading("Generazione scheda sopralluogo…");
    try {
      const res = await api.get(`/leads/${leadId}/sopralluogo/pad.pdf`, { responseType: "blob" });
      const burl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(burl, "_blank");
      toast.dismiss(t);
      setTimeout(() => URL.revokeObjectURL(burl), 60000);
    } catch (e) {
      toast.dismiss(t);
      toast.error(formatApiError(e.response?.data?.detail) || "Errore generazione PDF");
    }
  };
  return (
    <Card className="rounded-xl border-primary/30 bg-primary/[0.03] overflow-hidden" data-testid="report-operativo-panel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left hover:bg-primary/[0.05] transition-colors"
        data-testid="report-operativo-toggle"
      >
        <span className="flex items-center gap-2 font-heading font-semibold">
          <ClipboardCheck className="h-4 w-4 text-primary" />
          Report operativo — Riunione cantieri
        </span>
        <span className="flex items-center gap-1 text-xs text-muted-foreground">{open ? "Chiudi" : "Compila"}<ArrowRight className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-90" : ""}`} /></span>
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 space-y-5" data-testid="report-operativo-body">
          {RO_SECTIONS.map(([title, fields]) => (
            <div key={title} className="space-y-2">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">{title}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {fields.map(([k, lbl]) => (
                  <label key={k} className="flex flex-col gap-1 text-sm">
                    <span className="text-xs text-muted-foreground">{lbl}</span>
                    <Input
                      value={form[k] || ""}
                      onChange={(e) => set(k, e.target.value)}
                      data-testid={`ro-${k}`}
                      className="h-9"
                    />
                  </label>
                ))}
              </div>
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <Button onClick={save} disabled={saving} data-testid="report-operativo-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Check className="h-4 w-4 mr-1.5" />}
              Salva report
            </Button>
            <Button variant="outline" onClick={downloadPad} data-testid="report-operativo-pad-pdf">
              <FileDown className="h-4 w-4 mr-1.5" /> Scheda sopralluogo (PDF)
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function LeadOutlook({ lead, printPage = false, publicToken = null, refreshKey = 0, consulenzaMode = false, pdfExport = false, contractMode = false, documentHash = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errStatus, setErrStatus] = useState(null);
  const [mapError, setMapError] = useState(false);
  const [tick, setTick] = useState(0);
  const reloadOutlook = () => setTick((t) => t + 1);
  const leadId = lead?.id;

  const downloadFeasibilityPdf = async () => {
    const tab = window.open("", "_blank");
    if (tab) { try { tab.document.write("<!doctype html><meta charset='utf-8'><body style='font-family:system-ui,sans-serif;padding:24px;color:#334'>Generazione PDF di fattibilità in corso…</body>"); } catch (_e) { /* noop */ } }
    const t = toast.loading("Generazione PDF fattibilità…");
    try {
      const res = await api.get(`/leads/${leadId}/feasibility-pdf`, { responseType: "blob" });
      const burl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (tab) tab.location.href = burl; else window.location.href = burl;
      toast.dismiss(t);
      setTimeout(() => URL.revokeObjectURL(burl), 60000);
    } catch (e) {
      if (tab) tab.close();
      toast.dismiss(t);
      toast.error(formatApiError(e.response?.data?.detail) || "Errore generazione PDF");
    }
  };

  useEffect(() => {
    const url = publicToken
      ? (consulenzaMode ? `/public/consulenza/${publicToken}` : `/public/outlook/${publicToken}`)
      : `/leads/${leadId}/outlook`;
    setLoading(true);
    setErrStatus(null);
    api.get(url)
      .then((r) => setData(r.data))
      .catch((e) => setErrStatus(e.response?.status || 0))
      .finally(() => setLoading(false));
  }, [leadId, publicToken, refreshKey, tick]);

  if (loading) return <div className="flex h-48 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;
  if (errStatus === 410 || errStatus === 404) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-16 px-6" data-testid="outlook-link-error">
        <div className="h-14 w-14 rounded-full bg-secondary flex items-center justify-center mb-4">
          <Clock className="h-7 w-7 text-muted-foreground" />
        </div>
        <h2 className="font-heading font-bold text-xl mb-2">{errStatus === 410 ? "Link scaduto" : "Proposta non trovata"}</h2>
        <p className="text-sm text-muted-foreground max-w-md">
          {errStatus === 410
            ? "Questo link è scaduto (validità 10 giorni). Contatta il tuo consulente Torrisolare per ricevere una nuova proposta con un codice aggiornato."
            : "Il link non è valido. Verifica di aver aperto l'indirizzo corretto oppure richiedi una nuova proposta."}
        </p>
      </div>
    );
  }
  if (!data) return <p className="text-sm text-muted-foreground">Impossibile caricare l'outlook.</p>;

  const { cliente, impianto, consulenza, preventivo, piano, contract, prodotti, condizioni, tempi, roof, layout_images, allegati, colori, partner_disclaimer, legal_rete_disclaimer, report_consumi, manutenzione, preventivi_confronto, branding } = data;
  const leadObj = lead || { id: null, name: cliente?.name };
  const accepted = !!contract?.accettazione?.accettato;
  const acc = contract?.accettazione || {};
  const setAccettazione = (accettazione) =>
    setData((d) => ({ ...d, contract: { ...(d.contract || {}), accettazione } }));
  const nothing = !consulenza && !preventivo;
  const fin = preventivo?.finanziamento;
  const isConsulenza = consulenzaMode || data.mode === "consulenza";
  const isRete = !!(data && data.rete);
  const showColorePrices = !isConsulenza;
  const hasImpiantoProdotti = !!((impianto.n_pannelli || impianto.n_batterie || impianto.ricarica_model) || (prodotti && prodotti.length > 0));
  const mensileAutoPct = {};
  const mensileAutoKwh = {};
  const mensileAcqKwh = {};
  const mensileProdKwh = {};
  (consulenza?.mensile || []).forEach((m) => {
    const p = Number(m.produzione) || 0;
    const a = Number(m.autoconsumo) || 0;
    const c = Number(m.consumo) || 0;
    mensileAutoPct[m.mese] = p > 0 ? Math.round((a / p) * 100) : 0;
    mensileAutoKwh[m.mese] = Math.round(a);
    mensileAcqKwh[m.mese] = Math.max(0, Math.round(c - a));
    mensileProdKwh[m.mese] = Math.round(p);
  });

  const downloadSatellite = async () => {
    const view = esriRoofView(roof);
    if (!view) return;
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = view.url; });
      const canvas = document.createElement("canvas");
      canvas.width = view.W; canvas.height = view.H;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, view.W, view.H);
      const trace = (coords) => {
        const pts = (coords || []).map((c) => view.project(c.lat, c.lng));
        if (pts.length < 3) return false;
        ctx.beginPath();
        pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
        ctx.closePath();
        return true;
      };
      (view.falde || []).forEach((f, fi) => {
        const fc = FALDA_COLORS[fi % FALDA_COLORS.length];
        if (trace(f.coordinates)) { ctx.fillStyle = `${fc}40`; ctx.fill(); ctx.lineWidth = 2.5; ctx.strokeStyle = fc; ctx.stroke(); }
        (f.esclusioni || []).forEach((e) => { if (trace(e.coordinates)) { ctx.fillStyle = "rgba(220,38,38,0.33)"; ctx.fill(); ctx.lineWidth = 2; ctx.strokeStyle = "#DC2626"; ctx.stroke(); } });
      });
      canvas.toBlob((blob) => {
        if (!blob) { toast.error("Immagine satellitare non disponibile, riprova più tardi."); return; }
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `rilievo-satellitare-${(data?.cliente?.name || "tetto").replace(/\s+/g, "_")}.png`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 3000);
      }, "image/png");
    } catch (e) {
      toast.error("Immagine satellitare non disponibile, riprova più tardi.");
    }
  };

  return (
    <div className={`space-y-5${printPage ? " pdf-light" : ""}${printPage && isConsulenza && !piano ? " pdf-compact" : ""}`} id="outlook-print" data-testid="outlook-view">
      {!printPage && !publicToken && !isConsulenza && data.stage === "contratto" && (
        <ReportOperativoPanel leadId={leadId} initial={data.report_operativo} />
      )}
      {/* ===== PAGINA 1: Hero + Azienda + Confronto + Consulenza ===== */}
      <div className="space-y-5 pdf-fill-page" data-testid="outlook-pdf-page-1">
      {/* Hero */}
      <Card className="rounded-xl border-border overflow-hidden pdf-slot" data-testid="outlook-hero">
        <div className={`p-6 sm:p-8 ${printPage ? "bg-[#f2f3f5]" : "bg-card"}`}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              {branding && (branding.partner_logo_url || branding.partner_name) && !(printPage && consulenzaMode) && (
                <div className="flex items-center gap-2 mb-3" data-testid="outlook-cobrand">
                  {branding.partner_logo_url && <img src={branding.partner_logo_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="h-[72px] max-w-[300px] object-contain bg-white rounded px-3 py-1 border border-border" />}
                  <span className="text-xs text-muted-foreground">{branding.partner_name ? <><b className="text-foreground">{branding.partner_name}</b> in collaborazione con </> : ""}<b className="text-foreground">TORRI.SOLAR</b></span>
                </div>
              )}
              <Badge variant="outline" className="border-primary/40 text-primary mb-3 font-normal">
                <Sparkles className="h-3 w-3 mr-1" /> {isConsulenza ? "Analisi energetica personalizzata" : "Proposta energetica personalizzata"}
              </Badge>
              <h1 className="text-3xl sm:text-4xl font-heading font-bold leading-tight">{(cliente.entity_type === "azienda" ? (cliente.ragione_sociale || cliente.name) : cliente.name) || "Cliente"}</h1>
              {!isConsulenza && data.codice && <div className="text-xs font-mono tracking-wide text-primary mt-1" data-testid="outlook-codice">Proposta n. {data.codice}</div>}
              {cliente.contact_name && (
                <p className="text-sm text-foreground/80 mt-1.5" data-testid="outlook-contact">
                  Alla c.a. di <span className="font-semibold">{cliente.contact_name}</span>{cliente.contact_role ? ` — ${cliente.contact_role}` : ""}
                </p>
              )}
              {cliente.entity_type === "azienda" ? (
                cliente.partita_iva && (
                  <p className="text-sm text-muted-foreground mt-1" data-testid="outlook-fiscal">
                    <span>P.IVA {cliente.partita_iva}</span>
                  </p>
                )
              ) : (
                cliente.codice_fiscale && <p className="text-sm text-muted-foreground mt-1" data-testid="outlook-fiscal">Cod. Fiscale {cliente.codice_fiscale}</p>
              )}
              {(() => {
                const addr = [cliente.address, [cliente.cap, cliente.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") || cliente.city;
                if (!addr && !cliente.phone && !cliente.email) return null;
                return (
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-5 gap-y-0.5 text-sm text-muted-foreground" data-testid="outlook-cliente-recapiti">
                    {addr && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 shrink-0" /> {addr}{consulenza?.location && !cliente.address ? ` · ${consulenza.location}` : ""}</span>}
                    {cliente.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5 shrink-0" /> {cliente.phone}</span>}
                    {cliente.email && <span className="flex items-center gap-1 break-all"><Mail className="h-3.5 w-3.5 shrink-0" /> {cliente.email}</span>}
                  </div>
                );
              })()}
              {data.riferimento_qualificato && (
                <div className="text-sm text-muted-foreground mt-2" data-testid="outlook-riferimento">
                  <p className="flex items-center gap-1.5">
                    <UserCheck className="h-3.5 w-3.5 text-primary" /> Riferimento qualificato: <span className="font-semibold text-foreground">{data.riferimento_qualificato}</span>
                  </p>
                  {(data.riferimento_email || data.riferimento_phone) && (
                    <p className="text-xs mt-0.5 ml-5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                      {data.riferimento_phone && <a href={`tel:${data.riferimento_phone}`} className="hover:text-primary">{data.riferimento_phone}</a>}
                      {data.riferimento_email && <a href={`mailto:${data.riferimento_email}`} className="hover:text-primary">{data.riferimento_email}</a>}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-row flex-wrap items-center justify-center gap-x-4 gap-y-2" data-testid="outlook-hero-stats">
              {impianto.pv_kwp != null && (
                <div className="text-center">
                  <div className="text-3xl font-heading font-bold text-primary">{impianto.pv_kwp} kWp</div>
                  <div className="text-xs text-muted-foreground">Impianto fotovoltaico</div>
                </div>
              )}
              {impianto.storage_kwh ? (
                <div className="text-center border-l border-border pl-4">
                  <div className="text-3xl font-heading font-bold">{impianto.storage_kwh} kWh</div>
                  <div className="text-xs text-muted-foreground">Accumulo</div>
                </div>
              ) : null}
              {printPage && !isConsulenza && (() => {
                const qrUrl = data.proposal_url
                  || (publicToken ? `${typeof window !== "undefined" ? window.location.origin : ""}/p/${publicToken}` : (typeof window !== "undefined" ? window.location.href : ""));
                return qrUrl ? (
                  <div className="flex flex-col items-center gap-1 border-l border-border pl-4" data-testid="outlook-qr">
                    <span className="rounded-md bg-white p-1.5 border border-border">
                      <QRCodeSVG value={qrUrl} size={72} level="M" bgColor="#ffffff" fgColor="#1f1f1f" />
                    </span>
                    <span className="text-[10px] text-muted-foreground text-center max-w-[84px] leading-tight">Inquadra per aprire la proposta</span>
                  </div>
                ) : null;
              })()}
            </div>
          </div>
          {contract && (contract.caparra_pagata || contract.ordine_confermato) && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-lg bg-success/15 text-success px-3 py-1.5 text-sm font-medium" data-testid="outlook-contract">
              <CheckCircle2 className="h-4 w-4" /> Ordine confermato{contract.data_contratto && !isNaN(new Date(contract.data_contratto)) ? ` il ${new Date(contract.data_contratto).toLocaleDateString("it-IT")}` : ""}{contract.prezzo_finale ? ` · ${eur2(contract.prezzo_finale)}` : ""}
            </div>
          )}
          {!printPage && !publicToken && preventivo && (
            <OutlookShare leadId={leadId} defaultEmail={cliente?.email} />
          )}
          {!printPage && !publicToken && !isConsulenza && (
            <OrderConfirm leadId={leadId} contract={contract} defaultPrezzo={preventivo?.prezzo_iva_incluso} onDone={reloadOutlook} />
          )}
        </div>
      </Card>

      {/* Posizionamento aziendale */}
      <CompanyPositioning compact={isRete && pdfExport} />

      {!isConsulenza && preventivi_confronto?.length > 1 && (
        <PreventiviConfronto items={preventivi_confronto} publicToken={publicToken} leadId={leadId} locked={accepted} interactive={!printPage} onReload={reloadOutlook} />
      )}

      {nothing && !isConsulenza && !publicToken && (
        <Card className="rounded-xl border-border p-8 text-center" data-testid="outlook-empty">
          <Gauge className="h-8 w-8 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-muted-foreground">Genera il preventivo (scheda Preventivo) per completare la proposta finale. Se hai caricato la bolletta verrà mostrata anche l'analisi energetica.</p>
        </Card>
      )}

      {/* Consulenza energetica */}
      {consulenza && (
        <Section title="La tua consulenza energetica" icon={SunMedium} testid="outlook-consulenza" className="pdf-break-after">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 pdf-atomic">
            <Stat icon={Gauge} label="Copertura dei fabbisogni" value={`${consulenza.copertura_pct}%`} accent hint="Quota dei tuoi consumi annui coperta dall'energia prodotta dall'impianto." />
            <Stat icon={SunMedium} label="Autoconsumo ponderato" value={`${consulenza.autoconsumo_pct}%`} accent hint="Parte della produzione che usi direttamente in casa, anche grazie alla batteria." />
            <Stat icon={Zap} label="Produzione FV annua" value={kwh(consulenza.produzione_annua)} hint="Energia elettrica che i pannelli producono in un anno." />
            <Stat icon={Leaf} label="CO₂ risparmiata / anno" value={`${Number(consulenza.co2).toLocaleString("it-IT")} kg`} hint="Emissioni evitate ogni anno producendo energia pulita." />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-6 pt-6 border-t border-border pdf-atomic">
            <Stat icon={BatteryCharging} label="Energia autoconsumata" value={kwh(consulenza.autoconsumo)} hint="Energia prodotta e consumata da te: non la paghi in bolletta." />
            <Stat icon={ArrowRight} label="Immessa in rete" value={kwh(consulenza.immessi)} hint="Energia in eccesso ceduta alla rete e valorizzata con il ritiro dedicato." />
            <Stat icon={Gauge} label="Consumo annuo" value={kwh(consulenza.consumo_annuo)} hint="Il tuo fabbisogno elettrico annuo ricavato dalla bolletta." />
            <Stat icon={Zap} label="F1 / F2 / F3" value={<span className="text-lg">{consulenza.f1 ?? "—"}/{consulenza.f2 ?? "—"}/{consulenza.f3 ?? "—"}</span>} hint="Consumi in kWh per fascia oraria: F1 giorno feriale, F2 sera e sabato, F3 notte e festivi." />
          </div>
          {(consulenza.potenza_contatore_kw || consulenza.prezzo_energia_kwh) ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-6 pt-6 border-t border-border">
              {consulenza.potenza_contatore_kw ? <Stat icon={Zap} label="Potenza contatore" value={`${Number(consulenza.potenza_contatore_kw).toLocaleString("it-IT", { maximumFractionDigits: 2 })} kW`} hint="Potenza impegnata del tuo contatore, come da bolletta." /> : null}
              {consulenza.prezzo_energia_kwh ? <Stat icon={PiggyBank} label="Prezzo energia rilevato" value={`${Number(consulenza.prezzo_energia_kwh).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 3 })} €/kWh`} hint="Costo medio dell'energia in bolletta: ogni kWh autoconsumato vale questo risparmio." /> : null}
            </div>
          ) : null}
          {consulenza.ev_benefit?.enabled && (
            <div className="mt-6 pt-6 border-t border-border" data-testid="consulenza-ev-benefit">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3 pdf-subhead">Auto elettrica · risparmio ricarica dal fotovoltaico</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <BenefitCard color="#1FA36B" title="Auto elettrica" desc={`${consulenza.ev_benefit.model_label ? `${consulenza.ev_benefit.model_label} · ` : ""}Percorrenza ${Number(consulenza.ev_benefit.km_year).toLocaleString("it-IT")} km/anno · consumo medio ${Number(consulenza.ev_benefit.consumo_kwh_100).toLocaleString("it-IT")} kWh/100km. Coperti dal fotovoltaico ≈ ${Number(consulenza.ev_benefit.km_coperti).toLocaleString("it-IT")} km/anno (${consulenza.ev_benefit.copertura_pct}%), non coperti ≈ ${Number(consulenza.ev_benefit.km_scoperti).toLocaleString("it-IT")} km/anno.`} annuo={consulenza.ev_benefit.annuo} totale={consulenza.ev_benefit.totale} anni={consulenza.ev_benefit.anni} testid="benefit-ev-consulenza" />
              </div>
            </div>
          )}
        </Section>
      )}

      </div>
      {/* ===== PAGINA 2: Analisi consumi + Andamento mensile + Impianto + Prodotti ===== */}
      <div className="pdf-page-break space-y-5 pdf-fill-page" data-testid="outlook-pdf-page-2">
      <div className="space-y-5" data-testid="outlook-page-analisi">
      {/* Analisi dei consumi (dalla bolletta) */}
      {report_consumi && (report_consumi.mensile?.length > 0 || report_consumi.fasce?.f1_kwh != null) && (
        <Section title="Analisi dei tuoi consumi" icon={BarChart3} testid="outlook-report-consumi">
          <p className="text-xs text-muted-foreground mb-4 -mt-2">Profilo dei consumi elettrici ricostruito dalla tua bolletta: andamento mensile e ripartizione per fasce orarie F1 / F2 / F3.{report_consumi.consumo_annuo ? ` Consumo annuo stimato: ${kwh(report_consumi.consumo_annuo)}.` : ""}</p>
          <div className="grid grid-cols-2 gap-6 items-start">
            {report_consumi.mensile?.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 pdf-subhead text-center">Consumo mensile (kWh)</div>
                <div className={`${printPage ? "h-[264px]" : "h-[260px]"} w-full pdf-atomic`} data-testid="outlook-consumi-mensile-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={report_consumi.mensile} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C_GRID} vertical={false} />
                      <XAxis dataKey="mese" tick={{ fill: C_AXIS, fontSize: 11 }} axisLine={{ stroke: C_GRID }} tickLine={false} />
                      <YAxis tick={{ fill: C_AXIS, fontSize: 11 }} axisLine={false} tickLine={false} width={44} />
                      <Tooltip content={<ChartTooltip unit=" kWh" />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                      <Bar dataKey="consumo" name="Consumo" radius={[3, 3, 0, 0]} maxBarSize={26} isAnimationActive={!printPage}>
                        {report_consumi.mensile.map((m, i) => (
                          <Cell key={i} fill={m.origine === "reale" ? C_REALE : m.origine === "ricostruito" ? C_RID : C_STIM} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center justify-center gap-3 text-[10px] text-muted-foreground mt-1">
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: C_REALE }} />da bolletta</span>
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: C_RID }} />ricostruito</span>
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: C_STIM }} />stimato</span>
                </div>
              </div>
            )}
            {report_consumi.fasce?.f1_kwh != null && (
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 pdf-subhead text-center">Ripartizione fasce</div>
                <div className={`${printPage ? "h-[228px]" : "h-[210px]"} w-full pdf-atomic`} data-testid="outlook-fasce-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={[
                        { name: "F1", value: report_consumi.fasce.f1_kwh || 0 },
                        { name: "F2", value: report_consumi.fasce.f2_kwh || 0 },
                        { name: "F3", value: report_consumi.fasce.f3_kwh || 0 },
                      ]} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={printPage ? 48 : 66} innerRadius={printPage ? 26 : 38} isAnimationActive={!printPage}>
                        <Cell fill={C_AUTO} /><Cell fill={C_RID} /><Cell fill={C_CER} />
                      </Pie>
                      <Tooltip content={<ChartTooltip unit=" kWh" />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-1 text-xs mt-1 max-w-[210px] mx-auto">
                  <div className="flex items-center justify-center gap-2"><span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: C_AUTO }} />F1</span><span>{kwh(report_consumi.fasce.f1_kwh)} · {report_consumi.fasce.f1_pct}%</span></div>
                  <div className="flex items-center justify-center gap-2"><span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: C_RID }} />F2</span><span>{kwh(report_consumi.fasce.f2_kwh)} · {report_consumi.fasce.f2_pct}%</span></div>
                  <div className="flex items-center justify-center gap-2"><span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: C_CER }} />F3</span><span>{kwh(report_consumi.fasce.f3_kwh)} · {report_consumi.fasce.f3_pct}%</span></div>
                </div>
              </div>
            )}
          </div>
        </Section>
      )}

      {/* Andamento mensile (consumo ponderato) */}
      {consulenza?.mensile?.length > 0 && (
        <Section title="Andamento mensile · produzione e consumo" icon={BarChart3} testid="outlook-mensile">
          <p className="text-xs text-muted-foreground mb-4 -mt-2">Confronto mese per mese tra la produzione fotovoltaica, il consumo ponderato e la quota di energia autoconsumata. Sotto ogni mese è indicata la <b>percentuale di autoconsumo</b> (energia autoconsumata sulla produzione).</p>
          <div className={`${printPage ? "h-[312px]" : "h-[330px]"} w-full pdf-atomic`} data-testid="outlook-mensile-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={withAcquistata(consulenza.mensile)} margin={{ top: 8, right: 8, left: 34, bottom: 6 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C_GRID} vertical={false} />
                <XAxis dataKey="mese" interval={0} tick={{ fill: C_AXIS, fontSize: 11 }} axisLine={{ stroke: C_GRID }} tickLine={false} />
                <XAxis dataKey="mese" xAxisId="c_acq" interval={0} orientation="bottom" axisLine={false} tickLine={false} height={14} tick={{ fill: printPage ? "#9a3b2e" : "#F87171", fontSize: 8, fontWeight: 600 }} tickFormatter={(m) => (Number.isFinite(mensileAcqKwh[m]) ? mensileAcqKwh[m] : "")} label={<AcqKwhLabel fill={printPage ? "#9a3b2e" : "#F87171"} />} />
                <XAxis dataKey="mese" xAxisId="b_auto" interval={0} orientation="bottom" axisLine={false} tickLine={false} height={14} tick={{ fill: printPage ? "#3f7d3f" : "#4ADE80", fontSize: 8, fontWeight: 600 }} tickFormatter={(m) => (Number.isFinite(mensileAutoKwh[m]) ? mensileAutoKwh[m] : "")} label={<AutoKwhLabel fill={printPage ? "#3f7d3f" : "#4ADE80"} />} />
                <XAxis dataKey="mese" xAxisId="a_pct" interval={0} orientation="bottom" axisLine={false} tickLine={false} height={16} tick={{ fill: printPage ? "#1f1f1f" : "#FDD003", fontSize: 9, fontWeight: 700 }} tickFormatter={(m) => (Number.isFinite(mensileAutoPct[m]) ? mensileAutoPct[m] + "%" : "")} label={<AutoAxisLabel fill={printPage ? "#b8860b" : "#FDD003"} />} />
                <XAxis dataKey="mese" xAxisId="d_prod" interval={0} orientation="bottom" axisLine={false} tickLine={false} height={14} tick={{ fill: printPage ? "#C99700" : "#E0B341", fontSize: 8, fontWeight: 700 }} tickFormatter={(m) => (Number.isFinite(mensileProdKwh[m]) ? mensileProdKwh[m] : "")} label={<ProdKwhLabel fill={printPage ? "#C99700" : "#E0B341"} />} />
                <YAxis tick={{ fill: C_AXIS, fontSize: 11 }} axisLine={false} tickLine={false} width={44} />
                <Tooltip content={<ChartTooltip unit=" kWh" />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Bar dataKey="produzione" name="Produzione FV" fill={C_PROD} radius={[3, 3, 0, 0]} maxBarSize={22} isAnimationActive={!printPage} />
                <Bar dataKey="consumo" name="Consumo" fill={C_CONS} radius={[3, 3, 0, 0]} maxBarSize={22} isAnimationActive={!printPage} />
                <Line type="monotone" dataKey="autoconsumo" name="Autoconsumo" stroke={C_AUTO} strokeWidth={2.5} dot={{ r: 2.5, fill: C_AUTO }} isAnimationActive={!printPage} />
                <Line type="monotone" dataKey="acquistata" name="Energia acquistata" stroke={C_ACQ} strokeWidth={2} strokeDasharray="5 4" dot={false} isAnimationActive={!printPage} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <EnergyChartLegend data={consulenza.mensile} colors={{ prod: C_PROD, cons: C_CONS, auto: C_AUTO, acq: C_ACQ }} hasBattery={!!(impianto?.storage_kwh || consulenza.storage_kwh)} print={!!printPage} />
        </Section>
      )}
      </div>

      {/* Il tuo impianto + Prodotti e schede tecniche: pagina dedicata */}
      {hasImpiantoProdotti && (
      <div className="space-y-5" data-testid="outlook-page-impianto-prodotti">
      {(impianto.n_pannelli || impianto.n_batterie || impianto.ricarica_model || impianto.inverter_model) && (
        <Section title="Il tuo impianto chiavi in mano" icon={Zap} testid="outlook-impianto">
          <div className="rounded-lg border border-border divide-y divide-border pdf-atomic" data-testid="outlook-impianto-list">
            {impianto.n_pannelli ? (
              <div className="flex items-start gap-3 px-3 py-2.5" data-testid="outlook-impianto-pannello">
                <SunMedium className="h-4 w-4 text-primary mt-0.5 shrink-0" strokeWidth={1.5} />
                <div className="min-w-0">
                  <div className="font-heading font-semibold text-sm">{impianto.n_pannelli} moduli{impianto.pannello_model ? ` · ${impianto.pannello_model}` : ""}</div>
                  <div className="text-xs text-muted-foreground">{[impianto.pannello_wp ? `${impianto.pannello_wp} Wp` : null, impianto.pannello_tecnologia, impianto.pannello_efficienza ? `${impianto.pannello_efficienza}% eff.` : null, impianto.pannello_brand].filter(Boolean).join(" · ")}</div>
                  {impianto.pannello_descrizione && <div className="text-xs text-muted-foreground mt-1 whitespace-pre-line" data-testid="outlook-impianto-pannello-descrizione">{impianto.pannello_descrizione}</div>}
                  <GaranzieBadges list={impianto.pannello_garanzie} testid="outlook-garanzia-pannello" />
                </div>
              </div>
            ) : null}
            {impianto.n_batterie ? (
              <div className="flex items-start gap-3 px-3 py-2.5" data-testid="outlook-impianto-accumulo">
                <BatteryCharging className="h-4 w-4 text-primary mt-0.5 shrink-0" strokeWidth={1.5} />
                <div className="min-w-0">
                  <div className="font-heading font-semibold text-sm">Accumulo {impianto.capacita_totale_kwh} kWh{impianto.batteria_model ? ` · ${impianto.batteria_model}` : ""}</div>
                  <div className="text-xs text-muted-foreground">{[`${impianto.n_batterie} × ${impianto.capacita_modulo_kwh || (impianto.capacita_totale_kwh && impianto.n_batterie ? Math.round((impianto.capacita_totale_kwh / impianto.n_batterie) * 10) / 10 : "")} kWh`, impianto.batteria_tipo ? `${impianto.batteria_tipo}` : null, impianto.batteria_chimica, impianto.batteria_brand].filter(Boolean).join(" · ")}</div>
                  {impianto.batteria_descrizione && <div className="text-xs text-muted-foreground mt-1 whitespace-pre-line" data-testid="outlook-impianto-accumulo-descrizione">{impianto.batteria_descrizione}</div>}
                  <GaranzieBadges list={impianto.batteria_garanzie} testid="outlook-garanzia-accumulo" />
                </div>
              </div>
            ) : null}
            {impianto.inverter_model ? (
              <div className="flex items-start gap-3 px-3 py-2.5" data-testid="outlook-impianto-inverter">
                <Cpu className="h-4 w-4 text-primary mt-0.5 shrink-0" strokeWidth={1.5} />
                <div className="min-w-0">
                  <div className="font-heading font-semibold text-sm">Inverter · {impianto.inverter_model}</div>
                  <div className="text-xs text-muted-foreground">{[impianto.inverter_dettaglio, impianto.inverter_brand].filter(Boolean).join(" · ")}</div>
                  <GaranzieBadges list={impianto.inverter_garanzie} testid="outlook-garanzia-inverter" />
                </div>
              </div>
            ) : null}
            {impianto.ricarica_model ? (
              <div className="flex items-start gap-3 px-3 py-2.5" data-testid="outlook-impianto-ricarica">
                <Plug className="h-4 w-4 text-primary mt-0.5 shrink-0" strokeWidth={1.5} />
                <div className="min-w-0">
                  <div className="font-heading font-semibold text-sm">Stazione di ricarica{impianto.ricarica_potenza_kw ? ` · ${impianto.ricarica_potenza_kw} kW` : ""}</div>
                  <div className="text-xs text-muted-foreground">{[impianto.ricarica_model, impianto.ricarica_brand].filter(Boolean).join(" · ")}</div>
                  {impianto.ricarica_descrizione && <div className="text-xs text-muted-foreground mt-1 whitespace-pre-line" data-testid="outlook-impianto-ricarica-descrizione">{impianto.ricarica_descrizione}</div>}
                  <GaranzieBadges list={impianto.ricarica_garanzie} testid="outlook-garanzia-ricarica" />
                </div>
              </div>
            ) : null}
            {impianto.garanzia_impianto ? (
              <div className="flex items-start gap-3 px-3 py-2.5 bg-primary/5" data-testid="outlook-garanzia-impianto">
                <ShieldCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" strokeWidth={1.5} />
                <div className="font-heading font-semibold text-sm">Garanzia impianto chiavi in mano: {impianto.garanzia_impianto}</div>
              </div>
            ) : null}
          </div>
        </Section>
      )}

      {/* Prodotti con schede tecniche */}
      {prodotti && prodotti.length > 0 && (
        <Section title="Prodotti e schede tecniche" icon={PackageCheck} testid="outlook-prodotti">
          <ProductSlider prodotti={prodotti} printPage={printPage} />
        </Section>
      )}
      </div>
      )}
      </div>

      {/* ===== PAGINA 3: Fattibilità + Layout + Allegati + Tempi + Investimento ===== */}
      <div className="pdf-page-break space-y-5 pdf-fill-page" data-testid="outlook-pdf-page-3">
      {/* Analisi di fattibilità (satellite) */}
      {roof && (roof.falde || []).length > 0 && roof.summary && (
        <Section title="Analisi di fattibilità" icon={Satellite} testid="outlook-fattibilita" className={data.demo ? "relative overflow-hidden" : ""} action={(!publicToken && !printPage) ? (
          <Button size="sm" variant="outline" className="h-8 gap-1.5 shrink-0" onClick={downloadFeasibilityPdf} data-testid="feasibility-pdf-btn"><FileDown className="h-4 w-4" /> Stampa PDF</Button>
        ) : null}>
          <p className="text-xs text-muted-foreground mb-4 -mt-2">{hasPdfBg(roof.pdf_bg) ? "Layout dei moduli sulla tavola quotata in scala e superfici disponibili per l'installazione." : roof.draw_mode ? "Disegno in scala del tetto (griglia 1 m) e superfici disponibili per l'installazione dei moduli." : "Rilievo satellitare del tetto e superfici disponibili per l'installazione dei moduli."}</p>
          {hasDrawing(roof) ? <div className={pdfExport ? "pdf-atomic" : ""}><RoofMapView falde={roof.falde} panelLayout={combinedPanelLayout(roof)} annotations={roof.annotations} panelPhoto={roofPanelPhoto((!lead?.panel_auto && lead?.listino_panel) || roof.module)} pdfBg={roof.pdf_bg} origin={roof.center} drawMode={roof.draw_mode} pdfExport={pdfExport} compact={isRete} /></div> : !printPage ? <RoofMapView falde={roof.falde} panelLayout={combinedPanelLayout(roof)} annotations={roof.annotations} panelPhoto={roofPanelPhoto((!lead?.panel_auto && lead?.listino_panel) || roof.module)} /> : <>{!pdfExport && <div className="pdf-hide-map mb-4"><RoofMapView falde={roof.falde} panelLayout={combinedPanelLayout(roof)} annotations={roof.annotations} panelPhoto={roofPanelPhoto((!lead?.panel_auto && lead?.listino_panel) || roof.module)} /></div>}<div className="pdf-only-map" style={{ display: pdfExport ? "block" : "none" }}>{(() => {
            const view = esriRoofView(roof);
            if (!view) return null;
            if (mapError) return (
              <div className="w-full rounded-lg border border-border mb-4 p-6 text-center bg-muted/30" data-testid="fattibilita-map-error">
                <Satellite className="h-6 w-6 text-muted-foreground mx-auto mb-2" strokeWidth={1.5} />
                <p className="text-sm text-muted-foreground">Anteprima satellitare non disponibile al momento. I dati di superficie e potenza qui sotto restano validi.</p>
              </div>
            );
            return (
              <div className={`w-full rounded-lg border border-border mb-4 overflow-hidden pdf-atomic${isRete && pdfExport ? " max-w-[380px] mx-auto" : ""}`} data-testid="fattibilita-map">
                {publicToken ? (
                  /* PDF: immagine satellitare + poligoni COMPOSITATI lato server in un unico PNG piatto.
                     Nessuna composizione SVG in Chromium → nessuno sfasamento possibile tra disegno e mappa. */
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/public/roof-overlay/${publicToken}`} alt="Layout del tetto" className="w-full block" onError={() => setMapError(true)} data-testid="fattibilita-overlay-img" />
                ) : (
                <svg viewBox={`0 0 ${view.W} ${view.H}`} className="w-full block" style={{ display: "block" }}>
                  <image href={view.url} xlinkHref={view.url} x="0" y="0" width={view.W} height={view.H} preserveAspectRatio="none" crossOrigin="anonymous" onError={() => setMapError(true)} />
                  {view.falde.map((f, i) => {
                    const pts = (f.coordinates || []).map((c) => { const p = view.project(c.lat, c.lng); return `${p.x.toFixed(1)},${p.y.toFixed(1)}`; }).join(" ");
                    return (
                      <g key={f.id || i}>
                        {pts && <polygon points={pts} fill={`${FALDA_COLORS[i % FALDA_COLORS.length]}40`} stroke={FALDA_COLORS[i % FALDA_COLORS.length]} strokeWidth="2.5" />}
                        {(f.esclusioni || []).map((e, j) => {
                          const ep = (e.coordinates || []).map((c) => { const p = view.project(c.lat, c.lng); return `${p.x.toFixed(1)},${p.y.toFixed(1)}`; }).join(" ");
                          return ep ? <polygon key={j} points={ep} fill="#DC262655" stroke="#DC2626" strokeWidth="2" /> : null;
                        })}
                      </g>
                    );
                  })}
                  {combinedRoofPanels(roof).map((pn, k) => {
                    const cs = (pn.corners || []).map((c) => view.project(c.lat, c.lng));
                    if (cs.length < 3) return null;
                    const photo = roofPanelPhoto((!lead?.panel_auto && lead?.listino_panel) || roof.module);
                    const total = combinedRoofPanels(roof).length;
                    if (photo && cs.length >= 4 && total <= 300) {
                      const B = 100, p0 = cs[0], p1 = cs[1], p3 = cs[3];
                      const a = (p1.x - p0.x) / B, b = (p1.y - p0.y) / B, cc = (p3.x - p0.x) / B, d = (p3.y - p0.y) / B;
                      const qpts = cs.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
                      return <g key={`pn${k}`}><image href={photo} xlinkHref={photo} width={B} height={B} preserveAspectRatio="none" transform={`matrix(${a} ${b} ${cc} ${d} ${p0.x} ${p0.y})`} crossOrigin="anonymous" /><polygon points={qpts} fill="none" stroke="#FFFFFF" strokeWidth="0.19" strokeOpacity="0.45" /></g>;
                    }
                    const pts = cs.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
                    return pts ? <polygon key={`pn${k}`} points={pts} fill="#1E3A8ACC" stroke="#0B1B4D" strokeWidth="1" /> : null;
                  })}
                </svg>
                )}
              </div>
            );
          })()}</div></>}
          {((roof.annotations || []).length > 0 || (roof.falde || []).length > 1) && (() => {
            const LG = { cabina: ["#F97316", "Cabina elettrica"], cavidotto: ["#38BDF8", "Cavidotti"], locale_tecnico: ["#A855F7", "Locale tecnico / inverter"], parcheggio: ["#22C55E", "Area parcheggio mezzi di sollevamento"] };
            const tipi = Array.from(new Set((roof.annotations || []).map((a) => a.tipo))).filter((t) => LG[t]);
            return (
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-4 rounded-lg border border-border bg-muted/20 px-3 py-2.5" data-testid="roof-legend">
                <span className="w-full text-[10px] uppercase tracking-widest text-muted-foreground">Legenda elementi</span>
                {(roof.falde || []).map((f, i) => (<span key={f.id || i} className="inline-flex items-center gap-1.5 text-xs" data-testid={`roof-legend-falda-${i + 1}`}><span className="inline-block h-3 w-3 rounded-sm border border-black/20" style={{ background: FALDA_COLORS[i % FALDA_COLORS.length] }} /> {(f.copertura === "piano" ? "Superficie " : "Falda ") + (i + 1)}</span>))}
                {tipi.map((t) => (<span key={t} className="inline-flex items-center gap-1.5 text-xs"><span className="inline-block h-3 w-3 rounded-sm" style={{ background: LG[t][0] }} /> {LG[t][1]}</span>))}
              </div>
            );
          })()}
          {roofIsMisto(roof) ? (() => { const _c = roofPanelCounts(roof); return (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4 pdf-atomic" data-testid="fattibilita-misto">
              <FeasStat label="Tipo copertura" value="Mista (inclinato + piano)" />
              <FeasStat label="Moduli falde inclinate" value={_c.inc} />
              <FeasStat label="Moduli tetto piano" value={_c.flat} />
              <FeasStat label="Totale moduli" value={_c.inc + _c.flat} accent testid="fattibilita-moduli" />
              <FeasStat label="Potenza totale" value={`${Number(_c.incK + _c.flatK).toLocaleString("it-IT", { maximumFractionDigits: 2 })} kWp`} accent testid="fattibilita-kwp" />
              <FeasStat label="Superficie netta utilizzabile" value={m2(roof.summary.netta_m2)} />
            </div>
          ); })() : roof.roof_type === "piano" && roof.flat_layout?.compatible ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4 pdf-atomic" data-testid="fattibilita-prsg">
                <FeasStat label="Tipo copertura" value="Tetto piano" />
                <FeasStat label="Sistema" value={`${roof.flat_layout.system} · ${roof.flat_layout.variant}`} />
                <FeasStat label="Orientamento moduli" value="Landscape" />
                <FeasStat label="Inclinazione" value={`${roof.flat_layout.params?.tilt_deg}°`} />
                <FeasStat label="Azimut campo" value={`${Math.round(roof.flat_layout.params?.azimuth_deg)}°`} />
                <FeasStat label="N° file" value={roof.flat_layout.summary?.n_rows ?? "—"} />
                <FeasStat label="N° pannelli" value={roof.flat_layout.summary?.count ?? "—"} accent testid="fattibilita-moduli" />
                <FeasStat label="Potenza" value={`${Number(roof.flat_layout.summary?.kwp || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 })} kWp`} accent testid="fattibilita-kwp" />
                <FeasStat label="Superficie disponibile" value={m2(roof.flat_layout.summary?.surface_m2)} />
                <FeasStat label="Distanza libera D" value={`${Number(roof.flat_layout.geometry?.D_mm || 0).toLocaleString("it-IT")} mm`} />
                <FeasStat label="Passo file P" value={`${Number(roof.flat_layout.geometry?.P_mm || 0).toLocaleString("it-IT")} mm`} />
                <FeasStat label="Criterio antiombreggiamento" value="21 dic · mezz. solare locale" />
              </div>
              {roof.flat_layout.status === "verify" && (
                <div className="rounded-lg border border-amber-400/60 bg-amber-50 text-amber-800 px-3.5 py-2.5 text-xs mb-4 font-medium" data-testid="fattibilita-verify">⚠ Verifica tecnica consigliata — la distanza antiombreggiamento calcolata supera il campo standard previsto per il sistema PRSG (600–1200 mm). Configurazione valida ma da confermare con l'ufficio tecnico.{Number(roof.flat_layout.params?.tilt_deg) === 25 ? " Suggerimento: una staffa a 10° riduce la distanza tra le file e in genere rientra nello standard." : ""}</div>
              )}
            </>
          ) : (() => { const _c = roofPanelCounts(roof); const placed = _c.inc + _c.flat; const placedK = _c.incK + _c.flatK; const maxMod = roof.summary.moduli_max; const isDev = placed > 0 && Number(maxMod) !== placed; return (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4 pdf-atomic">
              <FeasStat label="Superficie proiettata" value={m2(roof.summary.proiettata_m2)} />
              <FeasStat label="Superficie reale inclinata" value={m2(roof.summary.reale_inclinata_m2)} />
              <FeasStat label="Superficie lorda" value={m2(roof.summary.lorda_m2)} />
              <FeasStat label="Superficie netta utilizzabile" value={m2(roof.summary.netta_m2)} accent />
              <FeasStat label="Moduli installabili (max)" value={roof.summary.moduli_max ?? "—"} accent testid="fattibilita-moduli" />
              <FeasStat label="Potenza installabile" value={`${Number(roof.summary.potenza_kwp || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 })} kWp`} accent testid="fattibilita-kwp" />
              {placed > 0 && (
                <div className="col-span-2 grid grid-cols-2 gap-3 rounded-xl border-2 border-[#FDD003]/60 p-2 -m-0.5 shadow-[0_0_18px_rgba(244,228,0,0.18)]" data-testid="fattibilita-layout-highlight">
                  <FeasStat label="Moduli effettivi a layout" value={placed} accent badge={isDev ? "sviluppato" : undefined} testid="fattibilita-moduli-layout" />
                  <FeasStat label="Potenza effettiva a layout" value={`${Number(placedK).toLocaleString("it-IT", { maximumFractionDigits: 2 })} kWp`} accent badge={isDev ? "sviluppato" : undefined} testid="fattibilita-kwp-layout" />
                </div>
              )}
            </div>
          ); })()}

          <div className="space-y-2 mb-3">
            {(roof.falde || []).map((f, i) => {
              const fcop = (f.copertura === "piano" || f.copertura === "inclinato") ? f.copertura : roof.roof_type;
              return (
              <div key={f.id || i} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs border border-border rounded-lg px-3 py-2" data-testid={`fattibilita-falda-${i}`}>
                <span className="font-semibold flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-primary" /> {fcop === "piano" ? "Superficie" : "Falda"} {i + 1}</span>
                {fcop !== "piano" && <span className="text-muted-foreground">Orientamento: <b className="text-foreground">{orientLabel(f.azimuth_deg)}</b></span>}
                {fcop !== "piano" && <span className="text-muted-foreground">Inclinazione: <b className="text-foreground">{f.tilt_deg}°</b></span>}
                {fcop === "piano" && <span className="text-muted-foreground">Copertura: <b className="text-foreground">Tetto piano</b></span>}
                <span className="text-muted-foreground">Superficie: <b className="text-foreground">{m2(f.proiettata_m2)}</b></span>
                {(f.esclusioni || []).length > 0 && (
                  <span className="text-red-500">{f.esclusioni.map((e) => EXCL_LABEL[e.tipo] || e.tipo).join(", ")}</span>
                )}
              </div>
              );
            })}
          </div>

          <div className="rounded-lg bg-primary/10 border border-primary/30 px-4 py-3 text-sm">
            {roofIsMisto(roof) ? (() => { const _c = roofPanelCounts(roof); return (
              <>Impianto a <b>copertura mista</b>: <b className="text-primary">{_c.inc + _c.flat} moduli</b> ({Number(_c.incK + _c.flatK).toLocaleString("it-IT", { maximumFractionDigits: 2 })} kWp) totali — <b className="text-primary">{_c.inc}</b> su falde inclinate e <b className="text-primary">{_c.flat}</b> su tetto piano (sistema PRSG).
              <LayoutSizingNote ll={impianto?.layout_lock} copertura={consulenza?.copertura_pct} fallback="Il dimensionamento in offerta resta calibrato sui tuoi consumi effettivi." /></>
            ); })() : roof.roof_type === "piano" && roof.flat_layout?.compatible ? (
              <>Impianto su <b>tetto piano</b> con sistema <b className="text-primary">{roof.flat_layout.variant}</b>: <b className="text-primary">{roof.flat_layout.summary?.count} moduli</b> ({Number(roof.flat_layout.summary?.kwp || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 })} kWp) su {roof.flat_layout.summary?.n_rows} file, inclinazione {roof.flat_layout.params?.tilt_deg}°.
              <span className="block text-xs text-muted-foreground mt-1">Distanza tra le file calcolata secondo il criterio antiombreggiamento TORRI (21 dicembre, mezzogiorno solare locale).</span>
              <LayoutSizingNote ll={impianto?.layout_lock} copertura={consulenza?.copertura_pct} fallback="Il dimensionamento in offerta resta calibrato sui tuoi consumi effettivi." /></>
            ) : (
              <>Potenza <b>installabile</b> stimata sul tetto: <b className="text-primary">{Number(roof.summary.potenza_kwp || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 })} kWp</b>.
            <LayoutSizingNote ll={impianto?.layout_lock} copertura={consulenza?.copertura_pct} fallback={`Il dimensionamento dell'impianto in offerta resta calibrato sui tuoi consumi effettivi${impianto?.pv_kwp ? ` (${Number(impianto.pv_kwp).toLocaleString("it-IT")} kWp proposti)` : ""}; questo valore indica la capacità massima ospitabile dalla copertura.`} /></>
            )}
          </div>
        </Section>
      )}

      {/* Layout pannelli fotovoltaici */}
      {!isConsulenza && (layout_images || []).length > 0 && (
        <Section title="Layout pannelli fotovoltaici" icon={LayoutGrid} testid="outlook-layout-pannelli">
          <p className="text-xs text-muted-foreground mb-4 -mt-2">Disposizione prevista dei moduli fotovoltaici sulla tua copertura.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {(layout_images || []).map((img) => {
              const src = publicToken
                ? `${API}/public/leads/${publicToken}/layout-image/${img.id}`
                : `${API}/files/${img.storage_path}?auth=${typeof window !== "undefined" ? localStorage.getItem("token") : ""}`;
              return (
                <img key={img.id} src={src} alt={img.filename || "Layout pannelli fotovoltaici"} className="w-full rounded-lg border border-border" data-testid={`outlook-layout-img-${img.id}`} />
              );
            })}
          </div>
        </Section>
      )}

      {/* Countdown validità link (solo vista consulenza/pre-outlook) */}
      {isConsulenza && data.expires_at && !(isRete && pdfExport) && (() => {
        const dl = Math.max(0, Math.ceil((new Date(data.expires_at) - new Date()) / 86400000));
        return (
          <Card className="rounded-xl border-amber-500/40 bg-amber-500/10 p-4" data-testid="consulenza-expiry">
            <div className="text-sm text-amber-700 dark:text-amber-400">
              ⏳ Questa analisi resta consultabile online fino al <b>{new Date(data.expires_at).toLocaleDateString("it-IT")}</b>
              {dl > 0 ? <> — mancano <b>{dl} giorni</b></> : <> — <b>scade oggi</b></>}. Il link <b>non è prorogabile</b>{isRete ? "." : <>: prenota un appuntamento per la consulenza completa.</>}
            </div>
          </Card>
        );
      })()}

      {/* Prenotazione consulenza (solo vista pubblica consulenza NON rete) */}
      {isConsulenza && publicToken && !isRete && (
        <BookingWidget token={publicToken} cliente={cliente} appointment={data.appointment} />
      )}

      {/* Sezione Rete: niente prenotazione, solo download PDF della consulenza */}
      {isConsulenza && publicToken && isRete && (
        <Card className="rounded-xl border-primary/30 bg-primary/5 p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 no-print" data-testid="rete-pdf-download">
          <div>
            <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Download className="h-5 w-5 text-primary" /> Scarica il PDF della consulenza</h3>
            <p className="text-sm text-muted-foreground mt-1">Salva o condividi l'analisi completa in formato PDF.</p>
          </div>
          <a href={`/c/${publicToken}/print`} target="_blank" rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground font-semibold px-5 py-2.5 text-sm hover:opacity-90 transition-opacity whitespace-nowrap"
            data-testid="rete-download-pdf-btn">
            <Download className="h-4 w-4" /> Scarica PDF
          </a>
        </Card>
      )}

      {/* Allegati alla proposta */}
      {(allegati || []).length > 0 && (
        <Section title="Allegati alla proposta" icon={Paperclip} testid="outlook-allegati">
          <p className="text-xs text-muted-foreground mb-4 -mt-2">Documenti di riferimento abbinati alla tua proposta, scaricabili.</p>
          <div className="space-y-2">
            {(allegati || []).map((a) => {
              const href = publicToken
                ? `${API}/public/leads/${publicToken}/attachment/${a.id}`
                : `${API}/files/${a.storage_path}?auth=${typeof window !== "undefined" ? localStorage.getItem("token") : ""}`;
              return (
                <a key={a.id} href={href} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-lg border border-border px-4 py-3 hover:border-primary/50 transition-colors" data-testid={`outlook-allegato-${a.id}`}>
                  <FileText className="h-5 w-5 text-primary shrink-0" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{a.oggetto}</div>
                    <div className="text-xs text-muted-foreground truncate">{a.filename}</div>
                  </div>
                  <Download className="h-4 w-4 text-muted-foreground shrink-0" />
                </a>
              );
            })}
          </div>
        </Section>
      )}

      {/* Tempi di realizzazione */}
      {tempi && (tempi.giorni || tempi.distanza_km) && !isConsulenza && (
        <Section title="Tempi di realizzazione" icon={Clock} testid="outlook-tempi">
          <p className="text-xs text-muted-foreground mb-2 -mt-2">Tempistiche stimate per l'installazione del tuo impianto chiavi in mano.</p>
          <div className={`grid gap-2 ${tempi.partner_avg_km != null && (!tempi.rete || tempi.partner_selected) ? "grid-cols-3" : "grid-cols-2"}`}>
            <div className="rounded-lg border border-border px-3 py-2 flex items-center gap-2.5" data-testid="outlook-tempi-giorni">
              <Clock className="h-4 w-4 text-primary shrink-0" strokeWidth={1.5} /><div className="min-w-0">
              <div className="font-heading font-bold text-sm leading-tight">{tempi.giorni} {Number(tempi.giorni) === 1 ? "giorno" : "giorni"}</div>
              <div className="text-[11px] text-muted-foreground leading-tight">Durata prevista dei lavori in cantiere</div></div>
            </div>
            <div className="rounded-lg border border-border px-3 py-2 flex items-center gap-2.5" data-testid="outlook-tempi-distanza">
              <MapPin className="h-4 w-4 text-primary shrink-0" strokeWidth={1.5} /><div className="min-w-0">
              <div className="font-heading font-bold text-sm leading-tight">{Number(tempi.hq_km ?? tempi.distanza_km).toLocaleString("it-IT")} km</div>
              <div className="text-[11px] text-muted-foreground leading-tight">Distanza dalla nostra sede al cantiere</div></div>
            </div>
            {tempi.partner_avg_km != null && (!tempi.rete || tempi.partner_selected) && (
              <div className="rounded-lg border border-border px-3 py-2 flex items-center gap-2.5" data-testid="outlook-tempi-partner-distanza">
                <Network className="h-4 w-4 text-primary shrink-0" strokeWidth={1.5} /><div className="min-w-0">
                <div className="font-heading font-bold text-sm leading-tight">{Number(tempi.partner_avg_km).toLocaleString("it-IT")} km</div>
                <div className="text-[11px] text-muted-foreground leading-tight">
                  {tempi.rete || tempi.partner_selected
                    ? "Distanza dell'installatore partner dal cantiere"
                    : publicToken
                    ? "Distanza media installatori di zona dal cantiere"
                    : `Distanza media dai ${tempi.num_partners || 3} partner di zona al cantiere`}
                </div></div>
              </div>
            )}
          </div>
        </Section>
      )}

      {/* L'investimento */}
      {preventivo && (
        <Section title="Il tuo investimento" icon={PiggyBank} testid="outlook-investimento">
          <div className="flex flex-wrap items-end justify-between gap-4 pdf-atomic">
            <div>
              <div className="text-xs text-muted-foreground">Prezzo chiavi in mano (IVA esclusa)</div>
              <div className="text-4xl font-heading font-bold text-primary" data-testid="outlook-prezzo">{eur(preventivo.prezzo)}</div>
              <div className="text-xs text-muted-foreground mt-1" data-testid="outlook-prezzo-ivato">IVA {preventivo.iva_pct ?? 10}% {eur(preventivo.iva_importo)} · Totale IVA inclusa {eur(preventivo.prezzo_iva_incluso)}</div>
            </div>
            {fin && fin.attivo && fin.rata_mensile && (
              <div className="rounded-lg bg-primary/10 border border-primary/30 px-5 py-3 text-right">
                <div className="text-xs text-muted-foreground">oppure a partire da</div>
                <div className="text-3xl font-heading font-bold text-primary">{eur2(fin.rata_mensile)}<span className="text-base font-normal">/mese</span></div>
                <div className="text-xs text-muted-foreground">{fin.durata_mesi} rate · {fin.tipo === "leasing" ? "Leasing" : "Findomestic"}</div>
              </div>
            )}
          </div>
        </Section>
      )}
      </div>

      {/* ===== PAGINA 4: Manutenzione + Ritorno + Proiezione + Servizi + Esclusioni ===== */}
      <div className="pdf-page-break space-y-5 pdf-fill-page" data-testid="outlook-pdf-page-4">
      {/* Manutenzione programmata */}
      {manutenzione && manutenzione.attiva && (
        <Section title="Manutenzione programmata" icon={Wrench} testid="outlook-manutenzione">
          {manutenzione.descrizione && <p className="text-sm text-muted-foreground mb-3">{manutenzione.descrizione}</p>}
          {manutenzione.partner_subordinata && <p className="text-sm text-muted-foreground mb-3" data-testid="outlook-man-partner-note">Il servizio di manutenzione programmata è affidato al <b>Partner di zona</b> ed è <b>subordinato alla sua accettazione</b>.</p>}
          {manutenzione.incluso && <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold px-3 py-1 mb-3" data-testid="outlook-man-incluso-badge"><Check className="h-3.5 w-3.5" /> Incluso nell'offerta{(manutenzione.anni || 0) > 0 ? ` · ${manutenzione.anni} ${manutenzione.anni === 1 ? "anno" : "anni"}` : ""}</div>}
          {!manutenzione.incluso && <div className="inline-flex items-center gap-1.5 rounded-full bg-secondary text-muted-foreground text-xs font-semibold px-3 py-1 mb-3" data-testid="outlook-man-esclusa-badge"><XCircle className="h-3.5 w-3.5" /> Esclusa dal prezzo proposto</div>}
          {(manutenzione.attivita || []).length > 0 && (
            <ul className="mb-4 space-y-1.5">
              {manutenzione.attivita.map((a) => (
                <li key={a.key} className="flex items-center gap-2 text-sm" data-testid={`outlook-man-att-${a.key}`}><Check className="h-4 w-4 text-primary" /> {a.label}</li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap gap-6 pdf-atomic">
            <div>
              <div className="text-xs text-muted-foreground">{manutenzione.incluso ? "Manutenzione (IVA 10% inclusa)" : "Costo annuo (IVA 10% inclusa)"}</div>
              <div className="text-2xl font-heading font-bold text-primary" data-testid="outlook-man-annuo">{manutenzione.incluso ? "Incluso" : (manutenzione.su_preventivo ? "Su preventivo" : eur(manutenzione.prezzo_finale_annuo_iva ?? manutenzione.costo_annuo_iva))}</div>
              <div className="text-xs text-muted-foreground">{manutenzione.incluso ? `${manutenzione.anni || 0} ${manutenzione.anni === 1 ? "anno" : "anni"} · valore ${eur(manutenzione.prezzo_finale_annuo_iva ?? manutenzione.costo_annuo_iva)}/anno` : (manutenzione.su_preventivo ? "Prezzo definito su preventivo dedicato" : `imponibile ${eur(manutenzione.prezzo_finale_annuo ?? manutenzione.costo_annuo)} · esclusa dal prezzo proposto`)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Costo per kWp</div>
              <div className="text-2xl font-heading font-bold">{eur2(manutenzione.prezzo_kwp)}<span className="text-sm font-normal text-muted-foreground">/kWp</span></div>
            </div>
          </div>
        </Section>
      )}

      {/* Ritorno economico */}
      {piano && (
        <Section title="Il tuo ritorno economico" icon={TrendingUp} testid="outlook-ritorno">

          <div className="mt-6" data-testid="outlook-benefici-dettaglio">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3 pdf-subhead">Dettaglio dei benefici energetici</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <BenefitCard color={C_AUTO} title="Autoconsumo" desc={`Energia autoconsumata × ${eur2(piano.prezzo_energia)}/kWh (prezzo bolletta)`} annuo={piano.autoconsumo_annuo} totale={piano.autoconsumo_totale} anni={piano.anni} testid="benefit-autoconsumo" />
              <BenefitCard color={C_RID} title="Ritiro dedicato (RID)" desc={`Energia immessa in rete × ${eur2(piano.rid_rate)}/kWh (tariffa RID)`} annuo={piano.rid_annuo} totale={piano.rid_totale} anni={piano.anni} testid="benefit-rid" />
              {piano.cer_attivo && (
                <BenefitCard color={C_CER} title="CER Nazionale TORRI" desc={`Energia immessa × fino a ${eur2(piano.cer_rate)}/kWh · governato a consuntivo`} annuo={piano.cer_annuo_medio} totale={piano.cer_totale} anni={piano.anni} testid="benefit-cer" />
              )}
              {piano.bonus > 0 && (
                <BenefitCard color="#C2703D" title="Detrazione fiscale" desc={`Recupero IRPEF del ${piano.bonus}% ripartito in ${piano.bonus_anni} anni`} annuo={piano.detrazione_annua} totale={piano.detrazione_totale} anni={piano.bonus_anni} testid="benefit-detrazione" />
              )}
              {piano.ev_benefit?.enabled && (
                <BenefitCard color="#1FA36B" title="Auto elettrica" desc={`${piano.ev_benefit.model_label ? `${piano.ev_benefit.model_label} · ` : ""}Percorrenza ${Number(piano.ev_benefit.km_year).toLocaleString("it-IT")} km/anno · consumo medio ${Number(piano.ev_benefit.consumo_kwh_100).toLocaleString("it-IT")} kWh/100km. Coperti dal fotovoltaico ≈ ${Number(piano.ev_benefit.km_coperti).toLocaleString("it-IT")} km/anno (${piano.ev_benefit.copertura_pct}%), non coperti ≈ ${Number(piano.ev_benefit.km_scoperti).toLocaleString("it-IT")} km/anno.`} annuo={piano.ev_benefit.annuo} totale={piano.ev_benefit.totale} anni={piano.ev_benefit.anni} testid="benefit-ev" />
              )}
            </div>
          </div>

          <p className="text-xs text-muted-foreground mt-4">{piano.bonus ? `Include la detrazione fiscale del ${piano.bonus}% (${eur(piano.detrazione_totale)}) ripartita in ${piano.bonus_anni} anni. Senza bonus il rientro è di ${piano.pbt_full} anni. ` : ""}L'autoconsumo è l'energia usata direttamente in casa; il RID e la CER si applicano all'energia in esubero immessa in rete (non autoconsumata né accumulata). Stima con degrado impianto 0,7%/anno. Valori indicativi.{piano.ev_delta_totale > 0 ? " Per l'auto elettrica, nel rientro economico è conteggiata solo la quota aggiuntiva rispetto al ritiro dedicato (per non duplicare l'energia immessa)." : ""}</p>
        </Section>
      )}

      {/* Proiezione economica anno per anno */}
      {piano?.proiezione?.length > 0 && (
        <Section title={`Proiezione economica a ${Math.min(20, piano.proiezione.length)} anni`} icon={TrendingUp} testid="outlook-proiezione">
          <p className="text-xs text-muted-foreground mb-4 -mt-2">Benefici cumulati nel tempo (autoconsumo, ritiro dedicato, {piano.cer_attivo ? "CER, " : ""}{piano.bonus ? `detrazione ${piano.bonus}% ripartita in ${piano.bonus_anni} anni` : "detrazione"}). La linea tratteggiata indica l'investimento ({eur(piano.prezzo_incl)}): il punto in cui le barre superano la linea è il rientro economico (BEP).{piano.finanziamento?.attivo ? ` La barra rossa mostra la rata ${piano.finanziamento?.tipo === "leasing" ? "del leasing" : "Findomestic"} annua (${eur2(piano.finanziamento.rata_mensile)}/mese per ${piano.finanziamento.durata_mesi} mesi).` : ""}</p>
          <div className="pdf-atomic">
          <div className={`${printPage ? "h-[320px]" : "h-[560px]"} w-full px-2 py-2`} data-testid="outlook-proiezione-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={(() => { let a = 0, r = 0, c = 0, d = 0, e = 0, rt = 0; return piano.proiezione.slice(0, 20).map((x) => { a += x.autoconsumo || 0; r += x.rid || 0; c += x.cer || 0; d += x.detrazione || 0; e += x.ev || 0; rt += x.rata || 0; return { ...x, autoconsumo: a, rid: r, cer: c, detrazione: d, ev: e, rata: rt }; }); })()} margin={{ top: 14, right: 20, left: 12, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C_GRID} vertical={false} />
                <XAxis dataKey="anno" tick={{ fill: C_AXIS, fontSize: 12 }} axisLine={{ stroke: C_GRID }} tickLine={false} />
                <YAxis tick={{ fill: C_AXIS, fontSize: 12 }} axisLine={false} tickLine={false} width={56} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip content={<ChartTooltip unit=" €" />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <ReferenceLine y={piano.prezzo_incl} stroke="#EF4444" strokeDasharray="5 4" strokeWidth={1.5}
                  label={{ value: "Investimento", position: "insideTopRight", fill: "#EF4444", fontSize: 11 }} />
                <Bar dataKey="autoconsumo" name="Autoconsumo" stackId="b" fill={C_AUTO} radius={[0, 0, 0, 0]} maxBarSize={44} isAnimationActive={!printPage} />
                <Bar dataKey="rid" name="Ritiro dedicato" stackId="b" fill={C_RID} radius={[0, 0, 0, 0]} maxBarSize={44} isAnimationActive={!printPage} />
                {piano.cer_attivo && (
                  <Bar dataKey="cer" name="CER TORRI" stackId="b" fill={C_CER} radius={[0, 0, 0, 0]} maxBarSize={44} isAnimationActive={!printPage} />
                )}
                {piano.bonus > 0 && (
                  <Bar dataKey="detrazione" name="Detrazione fiscale" stackId="b" fill="#C2703D" radius={[3, 3, 0, 0]} maxBarSize={44} isAnimationActive={!printPage} />
                )}
                {piano.ev_delta_totale > 0 && (
                  <Bar dataKey="ev" name="Auto elettrica" stackId="b" fill="#1FA36B" radius={[3, 3, 0, 0]} maxBarSize={44} isAnimationActive={!printPage} />
                )}
                {piano.finanziamento?.attivo && (
                  <Bar dataKey="rata" name={`Rata ${piano.finanziamento?.tipo === "leasing" ? "Leasing" : "Findomestic"}`} fill="#B45454" radius={[3, 3, 0, 0]} maxBarSize={44} isAnimationActive={!printPage} />
                )}
                <Line type="monotone" dataKey="cumulato" name="Beneficio cumulato" stroke={C_CUM} strokeWidth={2.5} dot={false} isAnimationActive={!printPage} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div data-testid="outlook-ritorno-kpi" className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 pt-4 border-t border-border">
            <Stat icon={PiggyBank} label="Risparmio medio annuo" value={eur(piano.risp_medio)} accent />
            <Stat icon={Clock} label="Tempo di rientro" value={(() => { const inv = piano.prezzo_incl || 0; const rows = piano.proiezione || []; let cum = 0, prev = 0, bep = null; for (let i = 0; i < rows.length; i++) { prev = cum; cum += (rows[i].autoconsumo || 0) + (rows[i].rid || 0) + (rows[i].cer || 0) + (rows[i].detrazione || 0) + (rows[i].ev || 0); if (cum >= inv && inv > 0) { bep = i + (inv - prev) / (cum - prev); break; } } return bep ? `${bep.toFixed(1)} anni` : `> ${rows.length} anni`; })()} accent />
            <Stat icon={TrendingUp} label={piano.bonus ? "Detrazione fiscale" : "Rendimento medio"} value={piano.bonus ? `${piano.bonus}% · ${piano.bonus_anni} anni` : `${piano.rend_lordo}%`} />
            <Stat icon={Sparkles} label={`Beneficio totale (${piano.anni} anni)`} value={eur(piano.tot_totale)} />
          </div>
          </div>
        </Section>
      )}

      {/* Servizi inclusi + Esclusioni: chiudono la PAGINA 4 */}
      {condizioni && (
        <div className="outlook-condizioni-group" data-testid="outlook-condizioni-group">
          <div className="grid grid-cols-2 gap-5 items-start">
            <Section title="Servizi inclusi" icon={Check} testid="outlook-servizi">
              <ul className="space-y-2.5">
                {condizioni.servizi_inclusi.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" strokeWidth={1.5} /> <span className="text-muted-foreground">{s}</span>
                  </li>
                ))}
              </ul>
            </Section>
            <Section title="Esclusioni" icon={XCircle} testid="outlook-esclusioni">
              <ul className="space-y-2.5">
                {condizioni.esclusioni.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <XCircle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" strokeWidth={1.5} /> <span className="text-muted-foreground">{s}</span>
                  </li>
                ))}
              </ul>
            </Section>
          </div>
        </div>
      )}
      </div>

      {/* ===== PAGINA 5: Condizioni → Modalità di pagamento → Firma ===== */}
      <div className="pdf-page-break space-y-5 pdf-fill-page" data-testid="outlook-pdf-page-5">
      {condizioni && condizioni.fornitura?.length > 0 && (
        <Section title="Condizioni di fornitura" icon={ScrollText} testid="outlook-fornitura">
              <div className="grid sm:grid-cols-2 gap-4">
                {condizioni.fornitura.map((c, i) => (
                  <div key={i} className="rounded-lg border border-border p-4">
                    <div className="font-semibold text-sm text-foreground mb-1">{c.titolo}</div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{c.testo}</p>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-4">Le condizioni generali di vendita complete sono riportate nella proposta contrattuale scaricabile.</p>
            </Section>
          )}

      {/* Note del preventivo */}
      {condizioni?.note && (
        <Section title="Note" icon={StickyNote} testid="outlook-note">
          <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{condizioni.note}</p>
        </Section>
      )}

      {/* Condizioni commerciali + Sottoscrizione: mantenute insieme nella STESSA pagina (PDF) */}
      <div className="outlook-firme-group">
      {(partner_disclaimer || legal_rete_disclaimer?.body || (condizioni && condizioni.commerciali?.length > 0)) && (
        <Section title="Condizioni commerciali" icon={Handshake} testid="outlook-commerciali">
          {partner_disclaimer && (
            <div className="rounded-lg border border-border bg-card px-4 py-3 mb-4 text-sm text-muted-foreground flex items-start gap-2.5" data-testid="partner-disclaimer">
              <svg className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <div>
                <div className="font-semibold mb-0.5 text-foreground">Proposta soggetta a conferma dell'azienda</div>
                <span>{partner_disclaimer}</span>
              </div>
            </div>
          )}
          {legal_rete_disclaimer?.body && (
            <p className="text-[11px] text-muted-foreground leading-relaxed mb-4" data-testid="outlook-legal-disclaimer">{legal_rete_disclaimer.body}</p>
          )}
          {condizioni?.commerciali?.length > 0 && (printPage ? (
            <div className="space-y-3">
              {condizioni.commerciali.map((c, i) => (
                <div key={i} data-testid={`outlook-commerciale-${i}`}>
                  <div className="text-sm font-semibold text-foreground">{c.titolo}</div>
                  <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">{c.testo}</p>
                </div>
              ))}
            </div>
          ) : (
            <Accordion type="single" collapsible className="w-full">
              {condizioni.commerciali.map((c, i) => (
                <AccordionItem key={i} value={`comm-${i}`} data-testid={`outlook-commerciale-${i}`}>
                  <AccordionTrigger className="text-sm font-medium text-left text-foreground hover:no-underline">{c.titolo}</AccordionTrigger>
                  <AccordionContent className="text-xs text-muted-foreground leading-relaxed">{c.testo}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ))}
          {printPage && condizioni?.vessatorie?.length > 0 && (
            <div className="mt-6 pt-4 border-t border-border pdf-atomic" data-testid="outlook-vessatorie-firma">
              {data.codice && <div className="text-[10px] font-mono tracking-wide text-muted-foreground mb-2">Proposta n. {data.codice}</div>}
              <p className="text-xs text-foreground leading-relaxed">
                Ai sensi e per gli effetti degli artt. 1341 e 1342 del Codice Civile, il Cliente dichiara di aver letto e di <span className="font-semibold">approvare specificamente</span> le seguenti clausole:
              </p>
              <p className="text-[11px] text-muted-foreground leading-relaxed mt-1">
                {condizioni.vessatorie.map((v) => v.titolo).join("; ")}.
              </p>
              {contractMode ? (
                <div className="mt-4 rounded-lg border border-primary/40 bg-primary/5 p-3" data-testid="outlook-vessatorie-firma-digitale">
                  <div className="text-xs font-semibold text-foreground">Clausole approvate specificamente</div>
                  <div className="text-[11px] text-muted-foreground mt-1">Approvate digitalmente da <span className="font-semibold text-foreground">{acc.nome || cliente?.name}</span>{acc.data ? ` il ${new Date(acc.data).toLocaleString("it-IT")}` : ""} · {(acc.clausole_vessatorie || []).length} clausole (artt. 1341-1342 c.c.).</div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-8 mt-6">
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Luogo e data</div>
                    <div className="mt-7 border-b border-foreground/60" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Il Cliente · firma per specifica approvazione</div>
                    <div className="mt-7 border-b border-foreground/60" />
                    {cliente?.name && <div className="text-[10px] text-muted-foreground mt-1">{cliente.name}</div>}
                  </div>
                </div>
              )}
            </div>
          )}
        </Section>
      )}

      {condizioni && (
        <div className="outlook-condizioni-group space-y-5">
          <Section title="Modalità di pagamento" icon={CreditCard} testid="outlook-pagamenti">
            {condizioni.pagamento_titolo && (
              <div className="font-semibold text-sm text-foreground mb-1.5" data-testid="outlook-pagamento-titolo">{condizioni.pagamento_titolo}</div>
            )}
            <p className="text-sm text-muted-foreground leading-relaxed">{condizioni.pagamento}</p>
            {fin && fin.attivo && fin.rata_mensile && (
              <div className="mt-4 rounded-lg bg-secondary/40 border border-border p-4 text-sm">
                <b>{fin.titolo || "Finanziamento Findomestic"}:</b> importo {eur2(fin.importo_finanziato)} — {fin.durata_mesi} rate da {eur2(fin.rata_mensile)}/mese (TAN {Number(fin.tasso_annuo * 100).toLocaleString("it-IT")}%), totale dovuto {eur2(fin.totale_dovuto)}.
              </div>
            )}
          </Section>
        </div>
      )}
      {/* Sottoscrizione della proposta (firma manuale) — solo nel PDF stampabile */}
      {printPage && !isConsulenza && (
        <Section title={contractMode ? "Sottoscrizione del contratto" : "Sottoscrizione della proposta"} icon={FileText} testid="outlook-firme">
          {data.codice && <div className="text-xs font-mono tracking-wide text-primary font-semibold mb-2" data-testid="outlook-firme-codice">Proposta n. {data.codice}</div>}
          {contractMode ? (
            <div data-testid="outlook-firma-digitale">
              <p className="text-xs text-muted-foreground mb-4 -mt-1">Il Cliente ha sottoscritto la presente proposta per accettazione mediante firma digitale, con registrazione di data, ora e indirizzo IP a fini probatori.</p>
              <div className="grid grid-cols-2 gap-8">
                <div className="space-y-2 text-xs">
                  <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Firmatario</div><div className="font-semibold text-foreground">{acc.nome || "—"}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Data e ora</div><div className="text-foreground">{acc.data ? new Date(acc.data).toLocaleString("it-IT") : "—"}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Indirizzo IP</div><div className="text-foreground">{acc.ip || "—"}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Clausole vessatorie approvate</div><div className="text-foreground">{(acc.clausole_vessatorie || []).length} clausole (art. 1341 c.c.)</div></div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Firma del Cliente</div>
                  {acc.firma_img ? <img src={acc.firma_img} alt="Firma digitale" className="h-20 rounded border border-border bg-white p-1 object-contain" data-testid="outlook-firma-img" /> : <div className="text-muted-foreground text-xs">—</div>}
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-4">Il Fornitore</div>
                  <div className="text-[10px] text-foreground mt-1 font-semibold">{data.venditore ? data.venditore.name : "TORRI.SOLAR"}</div>
                  <div className="text-[10px] text-muted-foreground">{data.venditore ? (data.venditore.lines || []).slice(1).join(" · ") : "Fier S.p.A."}</div>
                </div>
              </div>
              {documentHash && (
                <div className="mt-4 pt-2 border-t border-border text-[9px] text-muted-foreground break-all" data-testid="outlook-doc-hash">
                  <span className="font-semibold text-foreground">Codice di integrità documento (SHA-256):</span> {documentHash}
                </div>
              )}
              <p className="text-[9px] text-muted-foreground mt-2">Documento generato elettronicamente. La sottoscrizione è avvenuta mediante firma apposta su dispositivo, con registrazione di data, ora e indirizzo IP del firmatario a fini probatori.{documentHash ? " Il codice di integrità consente di verificare che il contenuto non sia stato alterato." : ""}</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-muted-foreground mb-6 -mt-1">Il Cliente dichiara di aver preso visione della presente proposta e di tutte le condizioni ivi riportate e la sottoscrive per accettazione.</p>
              <div className="mb-7">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Luogo e data</div>
                <div className="mt-7 border-b border-foreground/60 max-w-xs" />
              </div>
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Il Fornitore</div>
                  <div className="mt-9 border-b border-foreground/60" />
                  <div className="text-[10px] text-muted-foreground mt-1">{data.venditore ? data.venditore.name : "TORRI.SOLAR · Fier S.p.A."}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Il Cliente · firma per accettazione</div>
                  <div className="mt-9 border-b border-foreground/60" />
                  {cliente?.name && <div className="text-[10px] text-muted-foreground mt-1">{cliente.name}</div>}
                </div>
              </div>
            </>
          )}
        </Section>
      )}
      </div>
      </div>

      {/* Stampa (solo nella scheda Outlook, non in consulenza né nella pagina di stampa) */}
      {!printPage && !isConsulenza && (
        <Section title="Stampa" icon={FileText} testid="outlook-documenti">
          <div className="rounded-lg border border-dashed border-primary/40 bg-primary/5 p-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-semibold text-sm">Scarica la tua proposta commerciale</div>
              <p className="text-xs text-muted-foreground">Apri la versione stampabile e scaricala in PDF (A4).</p>
            </div>
            <Button asChild className="font-semibold" data-testid="outlook-print-btn">
              <a href={publicToken ? (isConsulenza ? `/c/${publicToken}/print` : `/p/${publicToken}/print`) : `/leads/${leadId}/print`} target="_blank" rel="noopener noreferrer">
                <Printer className="h-4 w-4 mr-2" /> Stampa
              </a>
            </Button>
          </div>
        </Section>
      )}

      {/* Sopralluogo tecnico a pagamento (prima del contratto) */}
      {!printPage && data.sopralluogo && (publicToken ? data.sopralluogo.offer_enabled : true) && (
        <SopralluogoCard sopralluogo={data.sopralluogo} publicToken={publicToken} leadId={leadObj.id} prezzo={preventivo?.prezzo_iva_incluso} onToggle={(sp) => setData((d) => ({ ...d, sopralluogo: sp }))} />
      )}

      {/* Procedi con l'ordine (firma + caparra) */}
      {!printPage && preventivo && (data.caparra > 0 || data.firma_senza_caparra) && (
        <OrderFlow lead={leadObj} publicToken={publicToken} caparra={data.caparra} noCaparra={!!data.firma_senza_caparra} contract={contract} vessatorie={condizioni?.vessatorie || []} onAccepted={setAccettazione} />
      )}

      {/* Footer legale consulenza — TORRI.SOLAR marchio di Fier S.p.A. + anagrafica completa */}
      {isConsulenza && !pdfExport && (
        <div className={`pt-6 mt-2 border-t ${printPage ? "border-neutral-200" : "border-border"} text-[10px] leading-relaxed ${printPage ? "text-neutral-500" : "text-muted-foreground"}`} data-testid="consulenza-legal-footer">
          {data.venditore ? (
            <>
              <div className={`font-semibold ${printPage ? "text-neutral-800" : "text-foreground"}`}>{data.venditore.name} — in collaborazione con TORRI.SOLAR</div>
              <div>{(data.venditore.lines || []).slice(1).join(" · ")}</div>
            </>
          ) : (<>
          <div className={`font-semibold ${printPage ? "text-neutral-800" : "text-foreground"}`}>TORRI.SOLAR è un marchio di Fier S.p.A.</div>
          <div>Fier S.p.A. a socio unico · Via Cremona, 123 - 25025 Manerbio (BS) · P.IVA/C.F. 03720770985 — Cap. soc. 500.000,00 € i.v.</div>
          <div>Registro AEE n. IT16120000009638 · Registro Pile e Accumulatori n. IT23100P00009100</div>
          </>)}
        </div>
      )}
    </div>
  );
}
